import { Fragment, useEffect, useState } from "react";
import { SuiAgentView } from "@/components/SuiAgentView";
import { isSuiAgent } from "@/lib/sui";
import { Link, useParams, useNavigate } from "react-router-dom";
import type { PolicyObject } from "@altheia-xyz/types";
import {
  Connection,
  PublicKey,
  SystemProgram,
  Transaction,
  clusterApiUrl,
  LAMPORTS_PER_SOL,
} from "@solana/web3.js";
import {
  TOKEN_PROGRAM_ID,
  getAssociatedTokenAddressSync,
  createAssociatedTokenAccountInstruction,
  createTransferCheckedInstruction,
  getMint,
  getAccount,
} from "@solana/spl-token";
import { useWallet } from "@solana/wallet-adapter-react";
import { AppChrome } from "@/components/Chrome";
import { Sigil, StatusRing, Hallmark, HallmarkBadge } from "@/components/Sigil";
import { TruncatedAddr, SOLSCAN, SOLSCAN_TX, PROGRAMS, fmtAmount, fmtRelTime } from "@/components/Primitives";
import { useAuth } from "@/components/providers/AuthProvider";
import { api, type AuditEvent, type AgentObject } from "@/lib/api";

const ENDPOINT = import.meta.env.VITE_SOLANA_RPC_URL ?? clusterApiUrl("devnet");
// devnet devUSDC (Orca). When flipping to mainnet, change to EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v.
const USDC_MINT = "BRjpCHtyQLNCo8gqRUr8jtdAj5AjPYQaoqbvcZiHok1k";
const SOL_MINT = "So11111111111111111111111111111111111111112";

type AuditRow = {
  id: string;
  tMin: number;
  action: string;
  mint: string;
  amount: number;
  target: string;
  result: "allowed" | "denied";
  reason: string;
  txSignature: string | null;
  slot: number | null;
};

function eventToRow(e: AuditEvent): AuditRow {
  const ad = e.action_descriptor ?? {};
  const created = new Date(e.created_at).getTime();
  const tMin = Math.max(1, Math.round((Date.now() - created) / 60000));
  return {
    id: e.id,
    tMin,
    action: ad.type ?? e.event_type,
    mint: ad.asset ?? e.asset_mint ?? "",
    amount: ad.amount ?? e.amount_token ?? 0,
    target: ad.target ?? e.destination ?? "",
    result: e.decision === "denied" ? "denied" : "allowed",
    reason: e.reason_detail ?? e.reason_code ?? "",
    txSignature: e.tx_signature,
    slot: e.slot,
  };
}

export function AgentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token, operator } = useAuth();
  const { sendTransaction } = useWallet();
  const [agent, setAgent] = useState<AgentObject | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRevoke, setShowRevoke] = useState(false);
  const [auditFilter, setAuditFilter] = useState<"all" | "allowed" | "denied">("all");
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [auditRows, setAuditRows] = useState<AuditRow[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editState, setEditState] = useState<
    "idle" | "building" | "signing" | "confirming" | "polling" | "done" | "error"
  >("idle");
  const [editErr, setEditErr] = useState<string | null>(null);
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false);
  const [rotateState, setRotateState] = useState<"idle" | "rotating" | "done" | "error">("idle");
  const [rotateErr, setRotateErr] = useState<string | null>(null);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [balance, setBalance] = useState<{
    smart_account: string;
    sol_lamports: number;
    sol_ui: number;
    tokens: Array<{ ata: string; mint: string; raw_amount: string; ui_amount: number | null; decimals: number }>;
  } | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceErr, setBalanceErr] = useState<string | null>(null);
  // Session-key wallet balance: this is where the agent actually spends from in
  // the demo flow (script signs Orca directly with the session key, not via
  // Swig SignV2). The shared Swig is separate; we don't surface it here to
  // avoid mixing two "vaults" on the per-agent page.
  const [sessionBalance, setSessionBalance] = useState<number | null>(null);
  const [sessionBalanceLoading, setSessionBalanceLoading] = useState(false);
  const [sessionBalanceErr, setSessionBalanceErr] = useState<string | null>(null);
  const [withdrawState, setWithdrawState] = useState<"idle" | "confirm" | "building" | "signing" | "confirming" | "done" | "error">("idle");
  const [withdrawErr, setWithdrawErr] = useState<string | null>(null);
  const [withdrawSig, setWithdrawSig] = useState<string | null>(null);
  const [depositOpen, setDepositOpen] = useState(false);
  const [reclaimOpen, setReclaimOpen] = useState(false);
  const [withdrawRecipient, setWithdrawRecipient] = useState("");
  const [cancelPendingState, setCancelPendingState] = useState<"idle" | "confirm" | "cancelling">("idle");
  const [cancelPendingErr, setCancelPendingErr] = useState<string | null>(null);
  const [pauseState, setPauseState] = useState<"idle" | "signing" | "confirming" | "error">("idle");
  const [pauseErr, setPauseErr] = useState<string | null>(null);
  const [revokeState, setRevokeState] = useState<"idle" | "building" | "signing" | "confirming" | "done" | "error">("idle");
  const [revokeErr, setRevokeErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !id) return;
    setLoading(true);
    api
      .getAgent(id, token)
      .then((r) => setAgent(r.agent))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [id, token]);

  // Audit refetch + 5s poll so SDK-emitted events appear without a manual reload.
  useEffect(() => {
    if (!token || !id) return;
    let cancelled = false;
    const fetchOnce = (showSpinner: boolean) => {
      if (showSpinner) setAuditLoading(true);
      api
        .getAgentAudit(id, token, { filter: auditFilter, limit: 50 })
        .then((r) => {
          if (!cancelled) setAuditRows(r.events.map(eventToRow));
        })
        .catch(() => {
          if (!cancelled) setAuditRows((rows) => rows);
        })
        .finally(() => {
          if (!cancelled && showSpinner) setAuditLoading(false);
        });
    };
    fetchOnce(true);
    const t = setInterval(() => fetchOnce(false), 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [id, token, auditFilter]);

  const fetchBalance = async () => {
    if (!token || !id) return;
    setBalanceLoading(true);
    setBalanceErr(null);
    try {
      const res = await api.getSmartAccountBalance(id, token);
      setBalance(res);
    } catch (e) {
      setBalanceErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBalanceLoading(false);
    }
  };
  // Fetch the session-key wallet's native SOL balance directly via RPC. This
  // is the wallet the agent's session-key signs from, distinct from the Swig.
  const fetchSessionBalance = async () => {
    if (!agent?.session_key_pubkey) return;
    setSessionBalanceLoading(true);
    setSessionBalanceErr(null);
    try {
      const conn = new Connection(ENDPOINT, "confirmed");
      const lamports = await conn.getBalance(new PublicKey(agent.session_key_pubkey));
      setSessionBalance(lamports / LAMPORTS_PER_SOL);
    } catch (e) {
      setSessionBalanceErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSessionBalanceLoading(false);
    }
  };
  useEffect(() => {
    if (!agent || isSuiAgent(agent)) return; // Sui agents have no Solana smart-account balance
    void fetchBalance();
    void fetchSessionBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token, agent]);

  const onWithdraw = async () => {
    if (!token || !id) {
      setWithdrawErr("not signed in");
      setWithdrawState("error");
      return;
    }
    setWithdrawState("building");
    setWithdrawErr(null);
    try {
      const customRecipient =
        withdrawRecipient.trim() && withdrawRecipient.trim() !== operator?.wallet_pubkey
          ? withdrawRecipient.trim()
          : undefined;
      const built = await api.buildWithdrawTx(id, token, customRecipient);
      setWithdrawState("signing");
      const sig = await signAndSendTx(built.transaction.base64);
      setWithdrawState("confirming");
      setWithdrawSig(sig);
      setWithdrawState("done");
      // Refresh balance after submission; webhook updates downstream.
      setTimeout(() => void fetchBalance(), 2500);
    } catch (e) {
      setWithdrawErr(e instanceof Error ? e.message : String(e));
      setWithdrawState("error");
    }
  };

  const savePolicy = async (newPolicy: PolicyObject) => {
    if (!token || !id || !agent) {
      setEditErr("not signed in");
      setEditState("error");
      return;
    }
    setEditErr(null);
    setEditState("building");

    const oldCommitment = agent.policy_commitment;

    try {
      const result = await api.updateAgent(id, { policy: newPolicy }, token);
      if (!result.transaction) {
        setEditErr("no transaction returned (name-only path)");
        setEditState("error");
        return;
      }

      setEditState("signing");
      await signAndSendTx(result.transaction.base64);

      setEditState("polling");
      const updated = await syncFromChain((a) => a.policy_commitment !== oldCommitment, 8);
      if (!updated) {
        setEditErr("Tx submitted; DB sync delayed — refresh in a minute");
        setEditState("error");
        return;
      }
      setAgent(updated);
      setEditState("done");
      setTimeout(() => {
        setEditOpen(false);
        setEditState("idle");
      }, 1500);
    } catch (e) {
      setEditErr(e instanceof Error ? e.message : "save failed");
      setEditState("error");
    }
  };

  const onRotateApiKey = async () => {
    if (!token || !id) return;
    setRotateState("rotating");
    setRotateErr(null);
    try {
      const res = await api.rotateApiKey(id, token);
      setNewApiKey(res.api_key.cleartext);
      setRotateState("done");
      setRotateConfirmOpen(false);
    } catch (e) {
      setRotateErr(e instanceof Error ? e.message : "rotate failed");
      setRotateState("error");
    }
  };

  const onCancelPending = async () => {
    if (!token || !id) return;
    setCancelPendingState("cancelling");
    setCancelPendingErr(null);
    try {
      await api.cancelPendingAgent(id, token);
      navigate("/dashboard");
    } catch (e) {
      setCancelPendingErr(e instanceof Error ? e.message : "cancel failed");
      setCancelPendingState("idle");
    }
  };

  // Generic: take base64 tx → submit through Phantom's RPC + sign in one step.
  // Phantom's sendTransaction is more reliable than client-side sendRawTransaction
  // (no CORS/rate-limit issues with the public Solana devnet RPC).
  const signAndSendTx = async (txBase64: string): Promise<string> => {
    const binary = atob(txBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const tx = Transaction.from(bytes);
    const conn = new Connection(ENDPOINT, "confirmed");
    return await sendTransaction(tx, conn);
  };

  // Force a fresh chain → DB sync via the backend. Used after a lifecycle tx
  // is submitted by Phantom; backend reads on-chain AgentAccount and overwrites
  // DB. Retries a few times because chain state takes ~1-3s to settle after submit.
  const syncFromChain = async (
    predicate: (a: AgentObject) => boolean,
    attempts = 5,
  ): Promise<AgentObject | null> => {
    if (!id || !token) return null;
    for (let i = 0; i < attempts; i++) {
      await new Promise((r) => setTimeout(r, i === 0 ? 1500 : 2500));
      try {
        const res = await api.syncAgent(id, token);
        if (predicate(res.agent)) return res.agent;
      } catch { /* AgentAccount fetch race; retry */ }
    }
    return null;
  };

  const onPauseToggle = async () => {
    if (!token || !id || !agent) return;
    const wasPaused = agent.status === "paused";
    setPauseState("signing");
    setPauseErr(null);
    try {
      const result = wasPaused
        ? await api.unpauseAgent(id, token)
        : await api.pauseAgent(id, token);
      await signAndSendTx(result.transaction.base64);
      setPauseState("confirming");
      // Wait for backend webhook to reflect status change.
      const updated = await syncFromChain((a) => a.status !== agent.status);
      if (updated) setAgent(updated);
      else {
        setPauseErr("Tx submitted; status sync delayed — refresh in a minute.");
        setPauseState("error");
        setTimeout(() => setPauseState("idle"), 4000);
        return;
      }
      setPauseState("idle");
    } catch (e) {
      setPauseErr(e instanceof Error ? e.message : "pause failed");
      setPauseState("error");
      setTimeout(() => setPauseState("idle"), 4000);
    }
  };

  const onRevoke = async (reasonCode: string) => {
    if (!token || !id || !agent) return;
    setRevokeState("building");
    setRevokeErr(null);
    try {
      const result = await api.revokeAgent(id, { reason_code: reasonCode }, token);
      setRevokeState("signing");
      const sig = await signAndSendTx(result.transaction.base64);
      setRevokeState("confirming");
      // Tell backend the tx is in flight so DB flips status without waiting for webhook.
      try { await api.confirmRevoke(id, sig, token); } catch { /* webhook will catch */ }
      const updated = await syncFromChain((a) => a.status === "revoked");
      if (updated) setAgent(updated);
      setRevokeState("done");
      setTimeout(() => {
        setShowRevoke(false);
        setRevokeState("idle");
      }, 1500);
    } catch (e) {
      setRevokeErr(e instanceof Error ? e.message : "revoke failed");
      setRevokeState("error");
    }
  };

  if (loading) {
    return (
      <AppChrome>
        <div className="adp-loading"><span className="kicker">Loading the chronicle…</span></div>
        <PageStyles />
      </AppChrome>
    );
  }
  if (error || !agent) {
    return (
      <AppChrome>
        <div className="alert">{error ?? "Agent not found"}</div>
        <Link to="/dashboard" className="btn btn--ghost">← Back to fleet</Link>
        <PageStyles />
      </AppChrome>
    );
  }

  const isPending = agent.is_pending === true;
  const isRevoked = agent.status === "revoked" || agent.status === "archived";
  const isArchived = agent.status === "archived";
  const isPaused = agent.status === "paused";
  const policy = (agent.policy as PolicyObject) ?? {};

  const lastDenied = auditRows.find((r) => r.result === "denied");
  const auditCounts = {
    all: auditRows.length,
    allowed: auditRows.filter((r) => r.result === "allowed").length,
    denied: auditRows.filter((r) => r.result === "denied").length,
  };
  const lastEventTMin = auditRows[0]?.tMin ?? null;

  const hasFunds = !!balance && (balance.tokens.length > 0 || balance.sol_lamports > 0);
  const showRecoveryBanner = isRevoked && !isArchived && hasFunds;

  // Sui agents render the read-only Sui view; the Solana JSX below never mounts for them.
  if (isSuiAgent(agent)) return <SuiAgentView agent={agent} token={token} />;

  return (
    <AppChrome>
      <div className={`adp ${isRevoked ? "adp--revoked" : ""}`}>
        {/* ZONE A — Identity */}
        <nav className="adp-crumb" aria-label="Breadcrumb">
          <Link to="/dashboard">← Agents</Link>
          <span className="adp-crumb__sep" aria-hidden>/</span>
          <span className="adp-crumb__name">{agent.name}</span>
        </nav>

        {/* Banners (priority: revoked-with-funds > recent-deny) */}
        {showRecoveryBanner && (
          <div className="adp-banner adp-banner--warning" role="status">
            <div className="adp-banner__body">
              <span className="kicker kicker--oxblood">
                Revoked{agent.revoked_at ? ` ${fmtRelTime(Math.max(1, Math.round((Date.now() - new Date(agent.revoked_at).getTime()) / 60000)))}` : ""}
                {agent.revoked_reason ? ` · ${agent.revoked_reason.replace(/_/g, " ")}` : ""}
                {" · funds remain in Swig"}
              </span>
              <span className="adp-banner__msg">
                Withdraw stranded balance to your wallet before archiving — only Role 0 (you) can move these funds.
              </span>
            </div>
          </div>
        )}
        {!showRecoveryBanner && lastDenied && agent.status === "active" && (
          <div className="adp-banner" role="status">
            <div className="adp-banner__body">
              <span className="kicker kicker--oxblood">
                {lastDenied.action.replace(/_/g, " ")} denied · {fmtRelTime(lastDenied.tMin)}
              </span>
              <span className="adp-banner__msg">
                {lastDenied.amount > 0 && (
                  <>
                    <span className="num">{fmtAmount(lastDenied.amount, lastDenied.mint)}</span>
                    {lastDenied.target && <> to <strong>{lastDenied.target}</strong></>}
                    {" — "}
                  </>
                )}
                <code>{lastDenied.reason || "denied"}</code>
              </span>
            </div>
            <a href="#chronicle" className="adp-banner__cta">Review chronicle →</a>
          </div>
        )}

        {/* ZONE 1 — Header (sigil + identity + actions + Holdings card) */}
        <header className={`adp-zone-header ${isPending ? "adp-zone-header--pending" : ""}`}>
          <div className="adp-head__sigil-frame">
            <Sigil
              seed={agent.on_chain_pubkey || agent.id}
              size={80}
              tone="bone"
              cracked={isRevoked}
              sweeping={agent.status === "active" && !isPending}
              strokeWidth={1.1}
            />
          </div>

          <div className="adp-head__id">
            <div className="adp-head__statusrow">
              {isPending ? (
                <>
                  <span className="adp-head__pending-dot" aria-hidden />
                  <span className="adp-head__statuslabel" style={{ color: "var(--bronze)" }}>PENDING</span>
                </>
              ) : (
                <>
                  <StatusRing status={agent.status as "active" | "paused" | "revoked"} size={11} />
                  <span className="adp-head__statuslabel" style={{ color: isRevoked ? "var(--oxblood)" : "var(--bone)" }}>
                    {agent.status.toUpperCase()}
                  </span>
                </>
              )}
            </div>
            <h1 className="adp-head__name">{agent.name}</h1>
            <div className="adp-head__meta">
              <span>{frameworkLabel(agent.framework)}</span>
              <span className="dot-sep" aria-hidden>·</span>
              <span>Registered {formatDate(agent.created_at)}</span>
              {!isPending && (
                <>
                  <span className="dot-sep" aria-hidden>·</span>
                  <span><span className="num">{auditCounts.all}</span> {auditCounts.all === 1 ? "event" : "events"}</span>
                </>
              )}
              {!isPending && lastEventTMin !== null && (
                <>
                  <span className="dot-sep" aria-hidden>·</span>
                  <span className="adp-head__last-inline">
                    <span className="live-dot" aria-hidden /> last action <span className="num">{fmtRelTime(lastEventTMin)}</span>
                  </span>
                </>
              )}
            </div>

            <div className="adp-head__refs" aria-label="On-chain references">
              <TruncatedAddr addr={agent.on_chain_pubkey} href={SOLSCAN(agent.on_chain_pubkey)} label="PDA" />
              <span className="dot-sep" aria-hidden>·</span>
              {agent.substrate_account_pubkey && (
                <>
                  <TruncatedAddr addr={agent.substrate_account_pubkey} href={SOLSCAN(agent.substrate_account_pubkey)} label="Swig" />
                  <span className="dot-sep" aria-hidden>·</span>
                </>
              )}
              <TruncatedAddr addr={agent.session_key_pubkey} href={SOLSCAN(agent.session_key_pubkey)} label="Session key" />
            </div>
          </div>

          {/* Kill-switch — center column, Altheia's moat. */}
          {!isPending && (
            <div className="adp-head__killswitch" aria-label="Kill-switch — operator-only">
              {!isRevoked ? (
                <>
                  <div className="adp-killswitch__row">
                    <button
                      className="btn btn--ghost adp-killswitch__btn"
                      onClick={() => void onPauseToggle()}
                      disabled={pauseState === "signing" || pauseState === "confirming"}
                    >
                      {pauseState === "signing" ? "Signing…" :
                        pauseState === "confirming" ? "Confirming…" :
                        isPaused ? "Unpause" : "Pause"}
                    </button>
                    <button className="btn btn--destructive adp-killswitch__btn" onClick={() => setShowRevoke(true)}>
                      <Hallmark size={10} tone="oxblood" /> Revoke
                    </button>
                  </div>
                  {pauseErr ? (
                    <span className="adp-killswitch__hint adp-killswitch__hint--err">⚠ {pauseErr}</span>
                  ) : (
                    <span className="adp-killswitch__hint">Operator-only · on-chain enforced</span>
                  )}
                </>
              ) : (
                <span className={`adp-head__terminal ${isArchived ? "adp-head__terminal--archived" : ""}`}>
                  {isArchived ? "Archived" : "Revoked"}
                </span>
              )}
            </div>
          )}

          {!isPending && (
            <div className="adp-head__right" aria-label="Holdings">
              <div className="adp-holdings-pane" aria-labelledby="holdings-title">
                <div className="adp-holdings-pane__head">
                  <h2 id="holdings-title" className="adp-holdings-pane__title">Holdings</h2>
                  <button
                    className="link-btn"
                    onClick={() => void fetchSessionBalance()}
                    disabled={sessionBalanceLoading}
                    aria-label="Refresh balance"
                  >
                    {sessionBalanceLoading ? "↻" : "↻ refresh"}
                  </button>
                </div>
                <p className="adp-holdings-pane__shared-note">
                  Agent spending wallet — the session key signs swaps from here. Per-agent, isolated.
                </p>

                <div className="adp-holdings-pane__rows">
                  {sessionBalanceLoading && sessionBalance == null ? (
                    <div className="adp-holdings-pane__msg">Reading on-chain…</div>
                  ) : sessionBalanceErr ? (
                    <div className="adp-holdings-pane__msg adp-holdings-pane__msg--err">
                      Couldn't fetch. <button className="link-btn" onClick={() => void fetchSessionBalance()}>Retry</button>
                    </div>
                  ) : sessionBalance == null || sessionBalance === 0 ? (
                    <div className="adp-holdings-pane__msg">
                      {isRevoked ? "Empty." : "Empty. Deposit to enable operations."}
                    </div>
                  ) : (
                    <div className="adp-holdings-pane__row">
                      <span className="adp-holdings-pane__asset">SOL</span>
                      <span className="num">{sessionBalance.toFixed(6)}</span>
                    </div>
                  )}
                </div>

                <div className="adp-holdings-pane__addr">
                  <TruncatedAddr addr={agent.session_key_pubkey} href={SOLSCAN(agent.session_key_pubkey)} label="Session key" />
                </div>

                <div className="adp-holdings-pane__actions">
                  {!isRevoked && (
                    <button className="adp-tiny-btn adp-tiny-btn--primary" onClick={() => setDepositOpen(true)}>
                      Deposit
                    </button>
                  )}
                  <button
                    className="adp-tiny-btn"
                    onClick={() => setReclaimOpen(true)}
                    disabled={sessionBalance == null || sessionBalance === 0}
                    title="Move SOL from this agent's session-key wallet back to your operator wallet. Requires the session key secret saved at registration."
                  >
                    Reclaim
                  </button>
                </div>
              </div>
            </div>
          )}
        </header>

        {isPending ? (
          <section className="adp-pending-card" aria-labelledby="pending-title">
            <span className="kicker kicker--bronze">⊙ Awaiting on-chain confirmation</span>
            <h2 id="pending-title" className="adp-pending-card__title">Sign the registration transaction in your wallet</h2>
            <p className="adp-pending-card__body">
              Until the on-chain register tx is confirmed, this agent's policy and Swig session-key constraints are not active. If you closed your wallet without signing, cancel below to remove the row + invalidate its API key.
            </p>
            {cancelPendingErr && <div className="adp-modal__err">{cancelPendingErr}</div>}
            <div className="adp-pending-card__actions">
              {cancelPendingState === "confirm" ? (
                <>
                  <button
                    className="btn btn--ghost"
                    onClick={() => setCancelPendingState("idle")}
                    disabled={cancelPendingState !== "confirm"}
                  >
                    Keep waiting
                  </button>
                  <button
                    className="btn btn--destructive"
                    onClick={() => void onCancelPending()}
                  >
                    Confirm cancel →
                  </button>
                </>
              ) : (
                <button
                  className="btn btn--destructive"
                  onClick={() => setCancelPendingState("confirm")}
                  disabled={cancelPendingState !== "idle"}
                >
                  {cancelPendingState === "cancelling" ? "Cancelling…" : "Cancel registration"}
                </button>
              )}
            </div>
          </section>
        ) : (
          <>
            {/* ZONE 2 — Policy (full width) */}
            <section className="adp-card" aria-labelledby="policy-title">
              <header className="adp-card__head">
                <h2 id="policy-title" className="adp-card__title">Policy</h2>
                {!isRevoked && (
                  <button className="btn btn--ghost btn--small" onClick={() => setEditOpen(true)}>
                    Edit policy
                  </button>
                )}
              </header>
              <div className="adp-card__body adp-card__body--policy">
                <div className="adp-policy-grid">
                  <PolicyCaps policy={policy} />
                  <AllowedPrograms policy={policy} />
                </div>
                <BlockedDestinations policy={policy} />
              </div>
              <footer className="adp-card__foot">
                <a href={SOLSCAN_TX(PROGRAMS.genesisDeployTx)} target="_blank" rel="noopener noreferrer">
                  <HallmarkBadge>Inscribed on Solana ↗</HallmarkBadge>
                </a>
                <span className="adp-card__foot-note">
                  <code>TokenRecurringLimit×2 · SolRecurringLimit · Program×3</code>
                </span>
              </footer>
            </section>

            {/* ZONE 3 — Audit log (full width) */}
            <section className="adp-card adp-card--audit" id="chronicle" aria-labelledby="audit-title">
              <header className="adp-card__head adp-card__head--audit">
                <h2 id="audit-title" className="adp-card__title">Audit log</h2>
                <div className="adp-pills" role="tablist" aria-label="Filter audit events">
                  <button
                    className={`adp-pill ${auditFilter === "all" ? "adp-pill--active" : ""}`}
                    onClick={() => setAuditFilter("all")}
                    role="tab"
                    aria-selected={auditFilter === "all"}
                  >
                    All <span className="num">{auditCounts.all}</span>
                  </button>
                  <button
                    className={`adp-pill ${auditFilter === "allowed" ? "adp-pill--active" : ""}`}
                    onClick={() => setAuditFilter("allowed")}
                    role="tab"
                    aria-selected={auditFilter === "allowed"}
                  >
                    Allowed <span className="num">{auditCounts.allowed}</span>
                  </button>
                  <button
                    className={`adp-pill adp-pill--denied ${auditFilter === "denied" ? "adp-pill--active" : ""}`}
                    onClick={() => setAuditFilter("denied")}
                    role="tab"
                    aria-selected={auditFilter === "denied"}
                  >
                    Denied <span className="num">{auditCounts.denied}</span>
                  </button>
                </div>
              </header>
              <div className="adp-card__body adp-card__body--audit">
                <ChronicleTable rows={auditRows} loading={auditLoading} expandedRow={expandedRow} setExpandedRow={setExpandedRow} />
              </div>
              <footer className="adp-card__foot adp-card__foot--note">
                <span className="adp-card__foot-note">Last 50 events · Merkle root committed every 100 blocks</span>
              </footer>
            </section>

            {/* ZONE 4 — API key (compact horizontal strip, full width, footer-feel) */}
            <section className="adp-apikey-strip" aria-labelledby="integration-title">
              <h2 id="integration-title" className="adp-apikey-strip__title">API key</h2>
              <p className="adp-apikey-strip__text">
                Bearer credential <code>ALTHEIA_API_KEY</code>. Rotating mints a new cleartext shown once and invalidates the old — no grace period.
              </p>
              <button
                className="btn btn--ghost btn--small adp-apikey-strip__btn"
                onClick={() => { setRotateErr(null); setRotateConfirmOpen(true); }}
                disabled={rotateState === "rotating" || isRevoked}
              >
                Rotate
              </button>
            </section>
          </>
        )}
      </div>

      {showRevoke && (
        <RevokeModal
          onClose={() => { if (revokeState === "idle" || revokeState === "error") { setShowRevoke(false); setRevokeState("idle"); setRevokeErr(null); } }}
          onConfirm={(reason) => void onRevoke(reason)}
          agentName={agent.name}
          state={revokeState}
          err={revokeErr}
        />
      )}
      {rotateConfirmOpen && (
        <RotateApiKeyConfirmModal
          rotating={rotateState === "rotating"}
          err={rotateErr}
          onClose={() => { if (rotateState !== "rotating") setRotateConfirmOpen(false); }}
          onConfirm={() => void onRotateApiKey()}
        />
      )}
      {newApiKey && (
        <ApiKeyRevealModal
          apiKey={newApiKey}
          copied={apiKeyCopied}
          onCopy={async () => { try { await navigator.clipboard.writeText(newApiKey); } catch { /* ignore */ } setApiKeyCopied(true); }}
          onClose={() => { setNewApiKey(null); setApiKeyCopied(false); setRotateState("idle"); }}
        />
      )}
      {withdrawState === "confirm" && (
        <WithdrawConfirmModal
          state={withdrawState}
          err={withdrawErr}
          onClose={() => setWithdrawState("idle")}
          onConfirm={() => void onWithdraw()}
          operatorWallet={operator?.wallet_pubkey ?? null}
          recipient={withdrawRecipient}
          setRecipient={setWithdrawRecipient}
          isRevoked={isRevoked}
        />
      )}
      {(withdrawState === "building" || withdrawState === "signing" || withdrawState === "confirming" || withdrawState === "error") && (
        <WithdrawProgressModal
          state={withdrawState}
          err={withdrawErr}
          onClose={() => setWithdrawState("idle")}
        />
      )}
      {editOpen && (
        <PolicyEditModal
          agent={agent}
          state={editState}
          err={editErr}
          onClose={() => {
            if (editState !== "signing" && editState !== "confirming" && editState !== "polling") {
              setEditOpen(false);
              setEditState("idle");
              setEditErr(null);
            }
          }}
          onSave={savePolicy}
        />
      )}
      {depositOpen && (
        <DepositModal
          address={agent.session_key_pubkey}
          agentName={agent.name}
          onClose={() => setDepositOpen(false)}
          onDeposited={() => {
            // Refresh session-key balance after the deposit lands; small delay
            // gives the RPC time to see the confirmed tx.
            setTimeout(() => void fetchSessionBalance(), 2500);
          }}
        />
      )}
      {reclaimOpen && (
        <ReclaimSessionKeyModal
          sessionKeyPubkey={agent.session_key_pubkey}
          defaultRecipient={operator?.wallet_pubkey ?? ""}
          onClose={() => setReclaimOpen(false)}
          onReclaimed={() => setTimeout(() => void fetchSessionBalance(), 2500)}
        />
      )}
      <PageStyles />
    </AppChrome>
  );
}

// Same catalog used at registration time. Duplicated here so the edit modal
// can render the same chip grid + verify-on-Solscan story; if this list grows,
// promote to a shared module.
type EditProgramCategory = "swap" | "perps" | "lend" | "token";
const EDIT_PROGRAM_CATALOG: { id: string; name: string; tag: string; category: EditProgramCategory; comingSoon?: boolean }[] = [
  { id: "JUP6LkbZbjS1jKKwapdHNy74zcZ3tLUZoi5QNyVTaV4", name: "Jupiter Aggregator v6", tag: "SWAP", category: "swap", comingSoon: true },
  { id: "675kPX9MHTjS2zt1qfr1NYHuzeLXfQM9H24wFSUt1Mp8", name: "Raydium AMM v4", tag: "SWAP", category: "swap", comingSoon: true },
  { id: "whirLbMiicVdio4qvUfM5KAg6Ct8VwpYzGff3uctyCc", name: "Orca Whirlpools", tag: "SWAP", category: "swap" },
  { id: "dRiftyHA39MWEi3m9aunc5MzRF1JYuBsbn6VPcn33UH", name: "Drift Protocol v2", tag: "PERPS", category: "perps", comingSoon: true },
  { id: "KLend2g3cP87fffoy8q1mQqGKjrxjC8boSyAYavgmjD", name: "Kamino Lend", tag: "LEND", category: "lend", comingSoon: true },
  { id: "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA", name: "SPL Token Program", tag: "TOKEN", category: "token" },
];
const EDIT_CATEGORY_FILTERS: { id: "all" | EditProgramCategory; label: string }[] = [
  { id: "all", label: "All" },
  { id: "swap", label: "Swap" },
  { id: "perps", label: "Perps" },
  { id: "lend", label: "Lend" },
  { id: "token", label: "Token" },
];

function isValidEditPubkey(s: string): boolean {
  if (s.length < 32 || s.length > 44) return false;
  return /^[1-9A-HJ-NP-Za-km-z]+$/.test(s);
}

function PolicyEditModal({
  agent,
  state,
  err,
  onClose,
  onSave,
}: {
  agent: AgentObject;
  state: "idle" | "building" | "signing" | "confirming" | "polling" | "done" | "error";
  err: string | null;
  onClose: () => void;
  onSave: (policy: PolicyObject) => Promise<void>;
}) {
  const policy = (agent.policy as PolicyObject) ?? {};
  const existingCaps = policy.asset_caps ?? {};
  const usdcCap = existingCaps[USDC_MINT] ?? existingCaps.USDC;
  const solCap = existingCaps[SOL_MINT] ?? existingCaps.SOL;

  const [usdcPerTx, setUsdcPerTx] = useState(String(usdcCap?.max_per_tx ?? ""));
  const [usdcPerDay, setUsdcPerDay] = useState(String(usdcCap?.max_per_day ?? ""));
  const [solPerTx, setSolPerTx] = useState(String(solCap?.max_per_tx ?? ""));
  const [solPerDay, setSolPerDay] = useState(String(solCap?.max_per_day ?? ""));
  const [allowedPrograms, setAllowedPrograms] = useState<Set<string>>(
    () => new Set(policy.allowed_programs ?? []),
  );
  const [programFilter, setProgramFilter] = useState<"all" | EditProgramCategory>("all");
  const [customProgram, setCustomProgram] = useState("");
  const [blockedDestList, setBlockedDestList] = useState<string[]>(policy.blocked_destinations ?? []);
  const [blockedDest, setBlockedDest] = useState("");
  const [localErr, setLocalErr] = useState<string | null>(null);

  const busy = state !== "idle" && state !== "error" && state !== "done";

  const togglePrograms = (id: string) => {
    setAllowedPrograms((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const addCustomProgram = () => {
    const trimmed = customProgram.trim();
    if (!trimmed) return;
    if (!isValidEditPubkey(trimmed)) {
      setLocalErr(`"${trimmed.slice(0, 12)}…" is not a valid Solana pubkey`);
      return;
    }
    setLocalErr(null);
    setAllowedPrograms((prev) => new Set(prev).add(trimmed));
    setCustomProgram("");
  };
  const addBlockedDest = () => {
    const trimmed = blockedDest.trim();
    if (!trimmed) return;
    if (!isValidEditPubkey(trimmed)) {
      setLocalErr(`"${trimmed.slice(0, 12)}…" is not a valid Solana pubkey`);
      return;
    }
    setLocalErr(null);
    setBlockedDestList((prev) => Array.from(new Set([...prev, trimmed])));
    setBlockedDest("");
  };

  const submit = () => {
    setLocalErr(null);
    const assetCaps: Record<string, { max_per_tx: number; max_per_day: number }> = {};
    const usdcTx = Number(usdcPerTx);
    const usdcDay = Number(usdcPerDay);
    if (Number.isFinite(usdcTx) && usdcTx > 0) {
      assetCaps[USDC_MINT] = {
        max_per_tx: usdcTx,
        max_per_day: Number.isFinite(usdcDay) && usdcDay > 0 ? usdcDay : usdcTx,
      };
    }
    const solTx = Number(solPerTx);
    const solDay = Number(solPerDay);
    if (Number.isFinite(solTx) && solTx > 0) {
      assetCaps[SOL_MINT] = {
        max_per_tx: solTx,
        max_per_day: Number.isFinite(solDay) && solDay > 0 ? solDay : solTx,
      };
    }
    if (Object.keys(assetCaps).length === 0) {
      setLocalErr("Set at least one per-tx cap (USDC or SOL)");
      return;
    }
    void onSave({
      asset_caps: assetCaps,
      allowed_programs: Array.from(allowedPrograms),
      blocked_destinations: blockedDestList,
    });
  };

  const customAdded = Array.from(allowedPrograms).filter(
    (id) => !EDIT_PROGRAM_CATALOG.some((p) => p.id === id),
  );

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="edit-title">
      <div className="modal-card modal-card--policy" onClick={(e) => e.stopPropagation()}>
        <h2 id="edit-title" className="modal-title">Edit policy — {agent.name}</h2>
        <p className="modal-lede">
          Phantom signs one transaction. The new <code>policy_commitment</code> + literal policy rewrite on-chain in place — no realloc, no new account.
        </p>

        {/* Per-asset caps */}
        <section className="pe-section">
          <span className="kicker kicker--bronze">⊙ Per-asset caps</span>
          <p className="pe-section__sub">
            Translates to Swig <code>TokenRecurringLimit</code>. Per-day defaults to per-tx if blank.
          </p>
          <div className="pe-cap-grid">
            <span className="kicker pe-cap-grid__h" />
            <span className="kicker pe-cap-grid__h">per tx</span>
            <span className="kicker pe-cap-grid__h">per day</span>

            <span className="pe-asset">USDC</span>
            <input type="number" min="0" value={usdcPerTx} onChange={(e) => setUsdcPerTx(e.target.value)} placeholder="100" disabled={busy} />
            <input type="number" min="0" value={usdcPerDay} onChange={(e) => setUsdcPerDay(e.target.value)} placeholder="500" disabled={busy} />

            <span className="pe-asset">SOL</span>
            <input type="number" min="0" step="0.1" value={solPerTx} onChange={(e) => setSolPerTx(e.target.value)} placeholder="0.5" disabled={busy} />
            <input type="number" min="0" step="0.1" value={solPerDay} onChange={(e) => setSolPerDay(e.target.value)} placeholder="2" disabled={busy} />
          </div>
        </section>

        {/* Allowed programs */}
        <section className="pe-section">
          <span className="kicker kicker--bronze">⊙ Allowed programs</span>
          <p className="pe-section__sub">
            Agent can only invoke programs listed here. Empty = every program allowed (wildcard).
          </p>
          <div className="pe-filters">
            {EDIT_CATEGORY_FILTERS.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`pe-filter ${programFilter === c.id ? "pe-filter--active" : ""}`}
                onClick={() => setProgramFilter(c.id)}
                disabled={busy}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="pe-program-grid">
            {EDIT_PROGRAM_CATALOG.filter((p) => programFilter === "all" || p.category === programFilter).map((p) => {
              const active = allowedPrograms.has(p.id);
              const comingSoon = p.comingSoon === true;
              return (
                <div
                  key={p.id}
                  className={`pe-program ${active ? "pe-program--active" : ""} ${comingSoon ? "pe-program--coming-soon" : ""}`}
                  title={comingSoon ? "Devnet liquidity unavailable. Mainnet roadmap." : undefined}
                >
                  <button
                    type="button"
                    className="pe-program__select"
                    onClick={() => {
                      if (comingSoon) return;
                      togglePrograms(p.id);
                    }}
                    aria-pressed={active}
                    aria-disabled={comingSoon}
                    disabled={busy}
                  >
                    <span className="pe-program__name">{p.name}</span>
                    <span className="pe-program__tag">{p.tag}</span>
                    <span className="pe-program__id">{p.id.slice(0, 6)}…{p.id.slice(-4)}</span>
                  </button>
                  <a
                    href={SOLSCAN(p.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pe-program__verify"
                    onClick={(e) => e.stopPropagation()}
                    title={`Verify ${p.name} on Solscan`}
                  >↗</a>
                  {comingSoon && <span className="pe-program__badge">Coming soon</span>}
                </div>
              );
            })}
          </div>
          <div className="pe-add-row">
            <input
              type="text"
              placeholder="Paste custom program ID (base58)"
              value={customProgram}
              onChange={(e) => setCustomProgram(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomProgram(); } }}
              disabled={busy}
            />
            <button type="button" className="btn btn--ghost btn--small" onClick={addCustomProgram} disabled={busy || !customProgram.trim()}>+ Add</button>
          </div>
          {customAdded.length > 0 && (
            <ul className="pe-chip-list">
              {customAdded.map((id) => (
                <li key={id} className="pe-chip">
                  <span className="num">{id.slice(0, 6)}…{id.slice(-4)}</span>
                  <button type="button" onClick={() => togglePrograms(id)} className="pe-chip__x" disabled={busy}>×</button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Blocked destinations */}
        <section className="pe-section">
          <span className="kicker kicker--bronze">⊙ Blocked destinations</span>
          <p className="pe-section__sub">
            Token transfers to these addresses revert on-chain regardless of caps.
          </p>
          <div className="pe-add-row">
            <input
              type="text"
              placeholder="Paste destination pubkey to block"
              value={blockedDest}
              onChange={(e) => setBlockedDest(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBlockedDest(); } }}
              disabled={busy}
            />
            <button type="button" className="btn btn--ghost btn--small" onClick={addBlockedDest} disabled={busy || !blockedDest.trim()}>+ Block</button>
          </div>
          {blockedDestList.length > 0 && (
            <ul className="pe-chip-list">
              {blockedDestList.map((id) => (
                <li key={id} className="pe-chip pe-chip--block">
                  <span className="num">{id.slice(0, 6)}…{id.slice(-4)}</span>
                  <button type="button" onClick={() => setBlockedDestList((prev) => prev.filter((x) => x !== id))} className="pe-chip__x" disabled={busy}>×</button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {localErr && <div className="edit-status edit-status--err">⚠ {localErr}</div>}
        {state !== "idle" && (
          <div className="edit-status" role="status" aria-live="polite">
            {state === "building" && "Building transaction…"}
            {state === "signing" && "Awaiting Phantom signature…"}
            {state === "confirming" && "Confirming on-chain…"}
            {state === "polling" && "Confirmed. Waiting for indexer…"}
            {state === "done" && "✓ Policy updated"}
            {state === "error" && err && <span className="edit-status--err">⚠ {err}</span>}
          </div>
        )}

        <div className="modal-actions">
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={submit} disabled={busy}>
            {state === "done" ? "Saved ✓" : busy ? "…" : "Sign + commit"}
          </button>
        </div>
      </div>
      <PolicyEditStyles />
    </div>
  );
}

function PolicyEditStyles() {
  return (
    <style>{`
      .modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: var(--s-4); }
      .modal-card { background: var(--bg-elev); border: 1px solid var(--line); padding: var(--s-6); width: 480px; max-width: 100%; max-height: 92vh; overflow-y: auto; display: flex; flex-direction: column; gap: var(--s-4); }
      .modal-card--policy { width: 720px; gap: var(--s-3); }
      .modal-title { font: var(--type-display-section); margin: 0; }
      .modal-lede { color: var(--slate); font: var(--type-body-small); margin: 0; }
      .modal-lede code { font: var(--type-mono-address); color: var(--bronze); background: var(--bronze-tint); padding: 1px 6px; }
      .edit-status { padding: var(--s-3); border-left: 2px solid var(--bronze); background: var(--bronze-tint); font: var(--type-body-small); }
      .edit-status--err { color: var(--oxblood); border-left-color: var(--oxblood); background: var(--oxblood-tint); }
      .modal-actions { display: flex; justify-content: flex-end; gap: var(--s-3); padding-top: var(--s-3); border-top: 1px solid var(--line-soft); }

      .pe-section { display: flex; flex-direction: column; gap: var(--s-2); padding: var(--s-3) var(--s-4); background: var(--bg); border: 1px solid var(--line-soft); }
      .pe-section__sub { color: var(--slate); font: var(--type-body-small); margin: 0; }
      .pe-section__sub code { font: var(--type-mono-address); color: var(--bronze); background: var(--bronze-tint); padding: 1px 6px; }

      .pe-cap-grid { display: grid; grid-template-columns: 80px 1fr 1fr; gap: var(--s-2) var(--s-3); align-items: center; margin-top: var(--s-1); }
      .pe-cap-grid__h { color: var(--slate-2); }
      .pe-cap-grid input { background: var(--bg); border: 1px solid var(--line); padding: var(--s-2) var(--s-3); font: var(--type-body); color: var(--bone); width: 100%; min-width: 0; box-sizing: border-box; }
      .pe-cap-grid input:focus { outline: none; border-color: var(--bronze); }
      .pe-cap-grid input:disabled { opacity: 0.5; }
      .pe-asset { font: var(--type-mono-data); font-size: 11px; padding: 2px 8px; border: 1px solid var(--line); color: var(--bone); justify-self: start; }

      .pe-filters { display: flex; gap: var(--s-2); flex-wrap: wrap; }
      .pe-filter { padding: 4px 10px; background: transparent; border: 1px solid var(--line); font-family: var(--font-mono); font-size: 9.5px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--slate-2); cursor: pointer; transition: border-color 160ms, color 160ms, background 160ms; }
      .pe-filter:hover:not(:disabled) { border-color: var(--bone); color: var(--bone); }
      .pe-filter--active { border-color: var(--bronze); color: var(--bronze-deep); background: var(--bronze-tint); }
      .pe-filter:disabled { opacity: 0.4; cursor: not-allowed; }

      .pe-program-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: var(--s-2); }
      .pe-program { position: relative; display: flex; align-items: stretch; background: var(--bg-elev); border: 1px solid var(--line); transition: border-color 160ms; }
      .pe-program:hover { border-color: var(--bone); }
      .pe-program__select { flex: 1; display: flex; flex-direction: column; gap: 2px; padding: var(--s-2) var(--s-3); background: transparent; border: 0; text-align: left; cursor: pointer; color: inherit; }
      .pe-program__select:disabled { cursor: not-allowed; }
      .pe-program__name { font-family: var(--font-display); font-style: italic; font-size: 13px; color: var(--bone); line-height: 1.1; }
      .pe-program__tag { font-family: var(--font-mono); font-size: 8px; letter-spacing: 0.16em; color: var(--slate-2); margin-top: 2px; }
      .pe-program__id { font-family: var(--font-mono); font-size: 9px; color: var(--slate); margin-top: 3px; }
      .pe-program__verify { display: inline-flex; align-items: center; justify-content: center; width: 28px; border-left: 1px solid var(--line); color: var(--slate-2); font-family: var(--font-mono); font-size: 11px; }
      .pe-program__verify:hover { color: var(--bronze); background: var(--bronze-tint); }
      .pe-program--active { border-color: var(--bronze); background: var(--bronze-tint); }
      .pe-program--active .pe-program__name { color: var(--bronze-deep); }
      .pe-program--active::before { content: '✓'; position: absolute; top: 4px; right: 36px; color: var(--bronze); font-family: var(--font-mono); font-size: 11px; }
      .pe-program--coming-soon { opacity: 0.45; cursor: not-allowed; position: relative; }
      .pe-program--coming-soon:hover { background: inherit !important; border-color: inherit !important; }
      .pe-program--coming-soon .pe-program__select { cursor: not-allowed; }
      .pe-program__badge {
        position: absolute;
        top: -8px;
        right: -8px;
        background: var(--bronze);
        color: var(--text-on-ink);
        font-size: 9px;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        padding: 2px 6px;
        border-radius: 2px;
        font-family: var(--font-display);
      }

      .pe-add-row { display: flex; gap: var(--s-2); }
      .pe-add-row input { flex: 1; min-width: 0; background: var(--bg); border: 1px solid var(--line); padding: var(--s-2) var(--s-3); font: var(--type-mono-address); color: var(--bone); }
      .pe-add-row input:focus { outline: none; border-color: var(--bronze); }
      .pe-add-row input:disabled { opacity: 0.5; }

      .pe-chip-list { list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: var(--s-2); }
      .pe-chip { display: inline-flex; align-items: center; gap: var(--s-2); padding: 2px 6px 2px 8px; border: 1px solid var(--line); background: var(--bg); }
      .pe-chip .num { font-size: 11px; color: var(--bone); }
      .pe-chip--block { border-color: var(--oxblood); }
      .pe-chip--block .num { color: var(--oxblood); }
      .pe-chip__x { background: none; border: 0; color: var(--slate); cursor: pointer; padding: 0 4px; font-size: 14px; line-height: 1; }
      .pe-chip__x:hover { color: var(--oxblood); }
      .pe-chip__x:disabled { opacity: 0.4; cursor: not-allowed; }
    `}</style>
  );
}

const KNOWN_PROGRAMS: Record<string, { name: string; tag: string }> = {
  JUP6LkbZbjS1jKKwapdHNy74zcZ3tLUZoi5QNyVTaV4: { name: "Jupiter Aggregator v6", tag: "SWAP" },
  dRiftyHA39MWEi3m9aunc5MzRF1JYuBsbn6VPcn33UH: { name: "Drift Protocol v2", tag: "PERPS" },
  KLend2g3cP87fffoy8q1mQqGKjrxjC8boSyAYavgmjD: { name: "Kamino Lend", tag: "LEND" },
};

function PolicyCaps({ policy }: { policy: PolicyObject }) {
  const caps = policy.asset_caps ?? {};
  const rows = Object.entries(caps).map(([mint, cap]) => {
    const symbol =
      mint === "BRjpCHtyQLNCo8gqRUr8jtdAj5AjPYQaoqbvcZiHok1k" || mint === "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v" || mint === "USDC" ? "USDC" :
      mint === "So11111111111111111111111111111111111111112" || mint === "SOL" ? "SOL" :
      mint.length > 10 ? `${mint.slice(0, 4)}…${mint.slice(-4)}` : mint;
    return { symbol, perTx: cap.max_per_tx ?? null, perDay: cap.max_per_day ?? null };
  });

  return (
    <div className="adp-policy-section">
      <span className="kicker">Per-asset caps</span>
      {rows.length === 0 ? (
        <div className="adp-policy-empty">No asset caps in this policy.</div>
      ) : (
        <table className="adp-policy-table">
          <thead>
            <tr>
              <th></th>
              <th>per tx</th>
              <th>per day</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.symbol}>
                <td><span className="adp-policy-asset">{r.symbol}</span></td>
                <td className="num">{r.perTx ?? "—"}</td>
                <td className="num">{r.perDay ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AllowedPrograms({ policy }: { policy: PolicyObject }) {
  const ids = policy.allowed_programs ?? [];
  return (
    <div className="adp-policy-section">
      <span className="kicker">Allowed programs</span>
      {ids.length === 0 ? (
        <div className="adp-policy-empty">No program scope set — every program allowed by default.</div>
      ) : (
        <ul className="adp-programs">
          {ids.map((id) => {
            const meta = KNOWN_PROGRAMS[id];
            return (
              <li key={id} className="adp-programs__row">
                <span className="adp-programs__name">{meta?.name ?? "Unknown"}</span>
                {meta?.tag && <span className="adp-programs__tag">{meta.tag}</span>}
                <span className="adp-programs__addr">
                  <TruncatedAddr addr={id} href={SOLSCAN(id)} />
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function BlockedDestinations({ policy }: { policy: PolicyObject }) {
  const blocked = policy.blocked_destinations ?? [];
  return (
    <div className="adp-policy-section">
      <span className="kicker">Blocked destinations</span>
      {blocked.length === 0 ? (
        <div className="adp-policy-empty">none</div>
      ) : (
        <ul className="adp-blocked-list">
          {blocked.map((id) => (
            <li key={id} className="adp-blocked-chip">
              <TruncatedAddr addr={id} href={SOLSCAN(id)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChronicleTable({
  rows,
  loading,
  expandedRow,
  setExpandedRow,
}: {
  rows: AuditRow[];
  loading: boolean;
  expandedRow: number | null;
  setExpandedRow: (i: number | null) => void;
}) {
  if (loading && rows.length === 0) {
    return (
      <div className="adp-audit-table-wrap">
        <div className="adp-audit-empty">
          <span className="kicker">Loading audit events…</span>
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="adp-audit-table-wrap">
        <div className="adp-audit-empty">
          <span className="kicker">No audit events yet</span>
          <p className="adp-audit-empty__hint">
            Run an action through the SDK and it will appear here within seconds.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="adp-audit-table-wrap">
      <table className="adp-audit-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Action</th>
            <th className="adp-audit-table__num">Amount</th>
            <th>Mint</th>
            <th>Target</th>
            <th className="adp-audit-table__right">Result</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 20).map((r, i) => {
            const isExpanded = expandedRow === i;
            const isDenied = r.result === "denied";
            return (
              <Fragment key={r.id}>
                <tr
                  className={`${isDenied ? "adp-audit-row--denied" : ""} ${isExpanded ? "adp-audit-row--expanded" : ""}`}
                  onClick={() => setExpandedRow(isExpanded ? null : i)}
                >
                  <td className="num">{fmtRelTime(r.tMin)}</td>
                  <td><span className="adp-audit-table__action">{r.action}</span></td>
                  <td className="num adp-audit-table__num">{r.amount ? fmtAmount(r.amount, r.mint) : "—"}</td>
                  <td><span className="adp-audit-table__mint">{r.mint || "—"}</span></td>
                  <td>
                    {r.target || "—"}
                    {r.target && (
                      <a
                        className="adp-audit-table__link"
                        href={SOLSCAN(r.target)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        aria-label="View target on Solscan"
                      > ↗</a>
                    )}
                  </td>
                  <td className="adp-audit-table__right">
                    <span className={isDenied ? "adp-audit-table__result-denied" : "adp-audit-table__result-allowed"}>
                      {isDenied ? <><span className="deny-bar" /> DENIED</> : <><Hallmark size={10} tone="bronze" /> ALLOWED</>}
                      <span className="adp-audit-table__chev" aria-hidden>{isExpanded ? "▾" : "›"}</span>
                    </span>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className={`adp-audit-detail ${isDenied ? "adp-audit-detail--denied" : ""}`}>
                    <td colSpan={6}>
                      <div className="adp-audit-detail__grid">
                        <div>
                          <span className="kicker">Tx signature</span>
                          {r.txSignature ? (
                            <a
                              href={SOLSCAN_TX(r.txSignature)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="num adp-audit-detail__link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {r.txSignature.startsWith("sdk-deny-") ? "(SDK pre-flight)" : `${r.txSignature.slice(0, 8)}…${r.txSignature.slice(-4)} ↗`}
                            </a>
                          ) : (
                            <span className="num">—</span>
                          )}
                        </div>
                        <div>
                          <span className="kicker">Slot</span>
                          <span className="num">{r.slot != null ? r.slot.toLocaleString() : "—"}</span>
                        </div>
                        <div>
                          <span className="kicker">Result</span>
                          <span className={`num ${isDenied ? "adp-audit-detail__reason" : ""}`}>
                            {isDenied ? r.reason : "ok"}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RevokeModal({
  onClose,
  onConfirm,
  agentName,
  state,
  err,
}: {
  onClose: () => void;
  onConfirm: (reasonCode: string) => void;
  agentName: string;
  state: "idle" | "building" | "signing" | "confirming" | "done" | "error";
  err: string | null;
}) {
  const [confirmText, setConfirmText] = useState("");
  const [reasonCode, setReasonCode] = useState("operator_revoked");
  const busy = state !== "idle" && state !== "error";
  const armed = confirmText === agentName && !busy;
  return (
    <div className="adp-modal-bg" onClick={busy ? undefined : onClose} role="dialog" aria-modal="true" aria-labelledby="revoke-title">
      <div className="adp-modal" onClick={(e) => e.stopPropagation()}>
        <span className="kicker kicker--oxblood">⊗ Break the seal</span>
        <h2 id="revoke-title" className="adp-modal__title">Revoke <em>{agentName}</em>?</h2>
        <p className="adp-modal__body">
          This <strong>cannot be undone</strong>. The agent's session key will be removed from the Swig program. Subsequent signatures will fail on-chain. The chronicle gains one final inscribed row in oxblood.
        </p>
        <label className="adp-modal__confirm">
          <span className="kicker">Reason</span>
          <select
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
            disabled={busy}
            className="adp-modal__select"
          >
            <option value="operator_revoked">Operator decision</option>
            <option value="security_incident">Security incident</option>
            <option value="agent_misbehavior">Agent misbehavior</option>
            <option value="key_rotation">Key rotation</option>
            <option value="end_of_life">End of life</option>
          </select>
        </label>
        <label className="adp-modal__confirm">
          <span className="kicker">Type the agent name to confirm</span>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={agentName}
            disabled={busy}
            autoFocus
          />
        </label>
        {state !== "idle" && (
          <div className="edit-status" role="status" aria-live="polite">
            {state === "building" && "Building transaction…"}
            {state === "signing" && "Awaiting Phantom signature…"}
            {state === "confirming" && "Confirming on Solana…"}
            {state === "done" && "✓ Revoked"}
            {state === "error" && err && <span className="edit-status--err">⚠ {err}</span>}
          </div>
        )}
        <div className="adp-modal__actions">
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="btn btn--destructive"
            disabled={!armed}
            onClick={() => onConfirm(reasonCode)}
          >
            {state === "done" ? "Revoked ✓" : busy ? "…" : "Break the seal →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RotateApiKeyConfirmModal({
  rotating,
  err,
  onClose,
  onConfirm,
}: {
  rotating: boolean;
  err: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="adp-modal-bg" onClick={rotating ? undefined : onClose} role="dialog" aria-modal="true" aria-labelledby="rotate-title">
      <div className="adp-modal" onClick={(e) => e.stopPropagation()}>
        <span className="kicker kicker--bronze">⊙ Confirm rotation</span>
        <h2 id="rotate-title" className="adp-modal__title">Rotate API key?</h2>
        <p className="adp-modal__body">
          The current key is <strong>invalidated immediately</strong>. The agent will fail every <code>guard()</code> call until you copy the new key into its <code>.env</code> and restart. There is no grace period.
        </p>
        {err && <div className="adp-modal__err">{err}</div>}
        <div className="adp-modal__actions">
          <button className="btn btn--ghost" onClick={onClose} disabled={rotating}>Cancel</button>
          <button className="btn btn--primary" onClick={onConfirm} disabled={rotating}>
            {rotating ? "Rotating…" : "Rotate key"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ApiKeyRevealModal({
  apiKey,
  copied,
  onCopy,
  onClose,
}: {
  apiKey: string;
  copied: boolean;
  onCopy: () => void;
  onClose: () => void;
}) {
  return (
    <div className="adp-modal-bg" role="dialog" aria-modal="true" aria-labelledby="reveal-title">
      <div className="adp-modal" onClick={(e) => e.stopPropagation()}>
        <span className="kicker kicker--bronze">⊙ Operator-only · shown once</span>
        <h2 id="reveal-title" className="adp-modal__title">New API key</h2>
        <p className="adp-modal__body">
          Copy this <strong>now</strong> and paste it into your agent's <code>.env</code> as <code>ALTHEIA_API_KEY</code>. We only retain its hash; we cannot show this cleartext again.
        </p>
        <div className="adp-apikey-reveal">
          <code className="adp-apikey-reveal__code">{apiKey}</code>
          <button className="btn btn--ghost adp-apikey-reveal__copy" onClick={onCopy}>
            {copied ? "Copied ✓" : "Copy"}
          </button>
        </div>
        <div className="adp-modal__actions">
          <button className="btn btn--primary" onClick={onClose} disabled={!copied}>
            {copied ? "Saved — close" : "Copy first"}
          </button>
        </div>
      </div>
    </div>
  );
}

function WithdrawConfirmModal({
  err,
  onClose,
  onConfirm,
  operatorWallet,
  recipient,
  setRecipient,
}: {
  state: string;
  err: string | null;
  onClose: () => void;
  onConfirm: () => void;
  operatorWallet: string | null;
  recipient: string;
  setRecipient: (s: string) => void;
  isRevoked: boolean;
}) {
  const trimmed = recipient.trim();
  const validShape = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(trimmed);
  const isCustom = validShape && trimmed !== operatorWallet;
  const canSubmit = validShape;

  return (
    <div className="adp-modal-bg" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="withdraw-title">
      <div className="adp-modal" onClick={(e) => e.stopPropagation()}>
        <span className="kicker kicker--bronze">⊙ Withdraw assets</span>
        <h2 id="withdraw-title" className="adp-modal__title">Withdraw all assets</h2>
        <p className="adp-modal__body">
          Transfers SOL + every SPL token from your <strong>shared smart-account</strong> to the address below. This drains the pool every agent you own can spend from — not just this one. You sign as Role 0; agent session keys (Role 1) are unaffected. Altheia never signs.
        </p>

        <label className="adp-withdraw-input" htmlFor="withdraw-recipient">
          <span className="kicker">Destination</span>
          <div className="adp-withdraw-input__row">
            <input
              id="withdraw-recipient"
              type="text"
              className="adp-withdraw-input__field"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="Recipient pubkey (base58)"
              spellCheck={false}
              autoCapitalize="off"
              autoComplete="off"
              autoCorrect="off"
            />
            {operatorWallet && trimmed !== operatorWallet && (
              <button
                type="button"
                className="link-btn adp-withdraw-input__reset"
                onClick={() => setRecipient(operatorWallet)}
              >
                use my wallet
              </button>
            )}
          </div>
          {trimmed && !validShape && (
            <span className="adp-withdraw-input__hint adp-withdraw-input__hint--err">
              Doesn't look like a valid Solana base58 pubkey (32–44 chars).
            </span>
          )}
          {validShape && !isCustom && (
            <span className="adp-withdraw-input__hint">Sending to your connected operator wallet.</span>
          )}
        </label>

        {isCustom && (
          <div className="adp-withdraw-warn" role="alert">
            <span className="kicker kicker--oxblood">⚠ Custom recipient</span>
            <p>
              Funds will land at an address that is <strong>not your connected wallet</strong>. Verify the address character-by-character — there is no recovery if you typo. Common safe choices: a hardware wallet, a fresh hot wallet, or a multisig you control.
            </p>
          </div>
        )}

        {err && <div className="adp-modal__err">{err}</div>}
        <div className="adp-modal__actions">
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={onConfirm} disabled={!canSubmit}>
            Sign with Phantom →
          </button>
        </div>
      </div>
    </div>
  );
}

function WithdrawProgressModal({
  state,
  err,
  onClose,
}: {
  state: string;
  err: string | null;
  onClose: () => void;
}) {
  const label =
    state === "building" ? "Building transaction…" :
    state === "signing" ? "Awaiting your wallet signature…" :
    state === "confirming" ? "Confirming on Solana…" :
    state === "error" ? "Withdraw failed" :
    "Working…";
  return (
    <div className="adp-modal-bg" role="dialog" aria-modal="true" aria-labelledby="withdraw-progress-title">
      <div className="adp-modal" onClick={(e) => e.stopPropagation()}>
        <span className="kicker kicker--bronze">⊙ Withdraw progress</span>
        <h2 id="withdraw-progress-title" className="adp-modal__title">{label}</h2>
        {err && <div className="adp-modal__err">{err}</div>}
        {state === "error" && (
          <div className="adp-modal__actions">
            <button className="btn btn--ghost" onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * ReclaimSessionKeyModal — recover SOL from an agent's session-key wallet.
 *
 * The session-key wallet holds its own private key. The backend never had it
 * (generated client-side at registration, shown once). To reclaim, the operator
 * pastes the session key base58 they saved; we sign locally, transfer all SOL
 * (minus a fee buffer) to the operator's wallet, and confirm. Secret never
 * leaves the browser.
 */
function ReclaimSessionKeyModal({
  sessionKeyPubkey,
  defaultRecipient,
  onClose,
  onReclaimed,
}: {
  sessionKeyPubkey: string;
  defaultRecipient: string;
  onClose: () => void;
  onReclaimed: () => void;
}) {
  const [secret, setSecret] = useState("");
  const [recipient, setRecipient] = useState(defaultRecipient);
  const [state, setState] = useState<"idle" | "building" | "signing" | "confirming" | "done" | "error">("idle");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [txSig, setTxSig] = useState<string | null>(null);

  const inFlight = state === "building" || state === "signing" || state === "confirming";

  const onReclaim = async () => {
    setErrMsg(null);
    setTxSig(null);
    setState("building");
    try {
      // bs58.decode is the format the reveal modal emitted at registration.
      const bs58 = (await import("bs58")).default;
      let kp;
      try {
        const { Keypair } = await import("@solana/web3.js");
        kp = Keypair.fromSecretKey(bs58.decode(secret.trim()));
      } catch {
        throw new Error("invalid session key — paste the base58 from the reveal modal");
      }
      if (kp.publicKey.toBase58() !== sessionKeyPubkey) {
        throw new Error("pasted secret doesn't match this agent's session key");
      }
      let dest: PublicKey;
      try {
        dest = new PublicKey(recipient.trim());
      } catch {
        throw new Error("invalid recipient address");
      }

      const conn = new Connection(ENDPOINT, "confirmed");
      const lamports = await conn.getBalance(kp.publicKey);
      // Reserve ~5000 lamports for the tx fee (single signer transfer).
      const sendLamports = lamports - 5000;
      if (sendLamports <= 0) throw new Error("session key has no SOL to reclaim");

      const tx = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: kp.publicKey,
          toPubkey: dest,
          lamports: sendLamports,
        }),
      );
      setState("signing");
      const sig = await conn.sendTransaction(tx, [kp], { maxRetries: 3 });
      setState("confirming");
      setTxSig(sig);
      await conn.confirmTransaction(sig, "confirmed");
      setState("done");
      onReclaimed();
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : String(e));
      setState("error");
    }
  };

  return (
    <div className="adp-modal-bg" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="reclaim-title">
      <div className="adp-modal" onClick={(e) => e.stopPropagation()}>
        <span className="kicker kicker--bronze">⊙ Reclaim session-key SOL</span>
        <h2 id="reclaim-title" className="adp-modal__title">Reclaim agent funds</h2>
        <p className="adp-modal__body">
          Moves this agent's spending SOL back to your wallet. Paste the <strong>session key base58</strong> you saved from the registration reveal modal. The secret stays in your browser — Altheia never sees it.
        </p>

        <label className="adp-withdraw-input" htmlFor="reclaim-secret">
          <span className="kicker">Session key (base58 secret)</span>
          <div className="adp-withdraw-input__row">
            <input
              id="reclaim-secret"
              type="password"
              className="adp-withdraw-input__field"
              placeholder="paste from reveal modal"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              disabled={inFlight || state === "done"}
              autoComplete="off"
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
            />
          </div>
        </label>

        <label className="adp-withdraw-input" htmlFor="reclaim-recipient">
          <span className="kicker">Recipient (your wallet)</span>
          <div className="adp-withdraw-input__row">
            <input
              id="reclaim-recipient"
              type="text"
              className="adp-withdraw-input__field"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              disabled={inFlight || state === "done"}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
            />
          </div>
        </label>

        {errMsg && <div className="adp-modal__err">{errMsg}</div>}

        {state === "done" && txSig && (
          <div className="adp-modal__body" role="status">
            ✓ Reclaimed.{" "}
            <a href={SOLSCAN_TX(txSig)} target="_blank" rel="noopener noreferrer" className="link-btn">
              Solscan ↗
            </a>
          </div>
        )}

        <div className="adp-modal__actions">
          <button className="btn btn--ghost" onClick={onClose} disabled={inFlight}>
            {state === "done" ? "Close" : "Cancel"}
          </button>
          {state !== "done" && (
            <button
              className="btn btn--primary"
              onClick={() => void onReclaim()}
              disabled={!secret.trim() || !recipient.trim() || inFlight}
            >
              {state === "building" ? "Building…" :
               state === "signing" ? "Signing…" :
               state === "confirming" ? "Confirming…" :
               "Reclaim →"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Deposit modal — sends SOL or any SPL token from the operator's Phantom wallet
 * directly to the Swig smart-account. SPL transfers compute the Swig's ATA and
 * include a create-if-missing instruction (operator pays ~0.002 SOL rent the
 * first time a given mint is deposited).
 *
 * Custom-CA path fetches mint decimals on submit. Failed mint resolution surfaces
 * as an inline error rather than a thrown tx.
 */
type TokenSel = "SOL" | "USDC" | "CUSTOM";

function DepositModal({
  address,
  agentName,
  onClose,
  onDeposited,
}: {
  address: string;
  agentName: string;
  onClose: () => void;
  onDeposited: () => void;
}) {
  const { publicKey, sendTransaction } = useWallet();
  const [tokenSel, setTokenSel] = useState<TokenSel>("SOL");
  const [customMint, setCustomMint] = useState("");
  const [amount, setAmount] = useState("");
  const [state, setState] = useState<"idle" | "building" | "signing" | "confirming" | "done" | "error">("idle");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [txSig, setTxSig] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Wallet balance of currently-selected token. null = loading or untenable
  // (e.g. invalid custom mint, no ATA). Refetched on tokenSel/customMint change
  // and after a successful deposit (debit shows up).
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [walletBalanceLoading, setWalletBalanceLoading] = useState(false);

  useEffect(() => {
    if (!publicKey) { setWalletBalance(null); return; }
    let cancelled = false;
    setWalletBalanceLoading(true);
    setWalletBalance(null);
    (async () => {
      try {
        const conn = new Connection(ENDPOINT, "confirmed");
        if (tokenSel === "SOL") {
          const lamports = await conn.getBalance(publicKey);
          if (!cancelled) setWalletBalance(lamports / LAMPORTS_PER_SOL);
          return;
        }
        const mintStr = tokenSel === "USDC" ? USDC_MINT : customMint.trim();
        if (!mintStr) { if (!cancelled) setWalletBalance(null); return; }
        let mintPk: PublicKey;
        try { mintPk = new PublicKey(mintStr); }
        catch { if (!cancelled) setWalletBalance(null); return; }
        const mintInfo = await getMint(conn, mintPk);
        const ata = getAssociatedTokenAddressSync(mintPk, publicKey);
        try {
          const acct = await getAccount(conn, ata);
          if (!cancelled) {
            setWalletBalance(Number(acct.amount) / 10 ** mintInfo.decimals);
          }
        } catch {
          // No ATA = zero balance, not an error
          if (!cancelled) setWalletBalance(0);
        }
      } catch {
        if (!cancelled) setWalletBalance(null);
      } finally {
        if (!cancelled) setWalletBalanceLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [publicKey, tokenSel, customMint, state]);

  const onCopy = async () => {
    try { await navigator.clipboard.writeText(address); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const amountNum = Number(amount);
  const amountValid = amount !== "" && Number.isFinite(amountNum) && amountNum > 0;
  const customMintValid = (() => {
    if (tokenSel !== "CUSTOM") return true;
    if (!customMint.trim()) return false;
    try { new PublicKey(customMint.trim()); return true; } catch { return false; }
  })();
  const inFlight = state === "building" || state === "signing" || state === "confirming";
  const canSubmit = !!publicKey && amountValid && customMintValid && !inFlight && state !== "done";

  const onDeposit = async () => {
    if (!publicKey) {
      setErrMsg("connect wallet first");
      setState("error");
      return;
    }
    setErrMsg(null);
    setTxSig(null);
    setState("building");
    try {
      const conn = new Connection(ENDPOINT, "confirmed");
      const recipient = new PublicKey(address);
      const tx = new Transaction();

      if (tokenSel === "SOL") {
        const lamports = Math.round(amountNum * LAMPORTS_PER_SOL);
        if (lamports <= 0) throw new Error("amount must be > 0");
        tx.add(
          SystemProgram.transfer({
            fromPubkey: publicKey,
            toPubkey: recipient,
            lamports,
          }),
        );
      } else {
        const mintStr = tokenSel === "USDC" ? USDC_MINT : customMint.trim();
        const mint = new PublicKey(mintStr);
        // Fetch decimals from chain (USDC is 6 but we still verify against the
        // mint account — same code path covers custom mints with any decimals).
        const mintInfo = await getMint(conn, mint);
        const rawAmount = BigInt(Math.round(amountNum * 10 ** mintInfo.decimals));
        if (rawAmount <= 0n) throw new Error("amount too small for token decimals");

        const fromAta = getAssociatedTokenAddressSync(mint, publicKey);
        const toAta = getAssociatedTokenAddressSync(mint, recipient, true);

        // Verify sender ATA exists — clearer error than a generic tx failure.
        try {
          await getAccount(conn, fromAta);
        } catch {
          throw new Error(`you don't hold this token in your wallet`);
        }

        // Create the Swig's ATA if missing. Operator pays rent (~0.002 SOL).
        let toExists = true;
        try { await getAccount(conn, toAta); } catch { toExists = false; }
        if (!toExists) {
          tx.add(
            createAssociatedTokenAccountInstruction(
              publicKey, // payer
              toAta,
              recipient, // owner = Swig
              mint,
            ),
          );
        }

        tx.add(
          createTransferCheckedInstruction(
            fromAta,
            mint,
            toAta,
            publicKey,
            rawAmount,
            mintInfo.decimals,
            [],
            TOKEN_PROGRAM_ID,
          ),
        );
      }

      setState("signing");
      const sig = await sendTransaction(tx, conn);
      setTxSig(sig);
      setState("confirming");
      await conn.confirmTransaction(sig, "confirmed");
      setState("done");
      onDeposited();
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : String(e));
      setState("error");
    }
  };

  return (
    <div className="adp-modal-bg" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="deposit-title">
      <div className="adp-modal" onClick={(e) => e.stopPropagation()}>
        <span className="kicker kicker--bronze">⊙ Deposit to your smart account</span>
        <h2 id="deposit-title" className="adp-modal__title">Send SOL or SPL tokens</h2>
        <p className="adp-modal__body">
          Funds land in your shared Swig smart-account — pooled across all agents you own. Each agent can spend within its own policy caps; you (Role 0) can always withdraw.
        </p>

        <div className="adp-deposit__form">
          <label className="adp-deposit__field">
            <span className="adp-deposit__label">Token</span>
            <select
              className="adp-deposit__select"
              value={tokenSel}
              onChange={(e) => setTokenSel(e.target.value as TokenSel)}
              disabled={inFlight || state === "done"}
            >
              <option value="SOL">SOL</option>
              <option value="USDC">USDC</option>
              <option value="CUSTOM">Paste mint address…</option>
            </select>
          </label>

          {tokenSel === "CUSTOM" && (
            <label className="adp-deposit__field">
              <span className="adp-deposit__label">Mint address</span>
              <input
                type="text"
                className="adp-deposit__input adp-deposit__input--mono"
                placeholder="e.g. EPjFW…TDt1v"
                value={customMint}
                onChange={(e) => setCustomMint(e.target.value)}
                disabled={inFlight || state === "done"}
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="off"
              />
              {customMint && !customMintValid && (
                <span className="adp-deposit__hint adp-deposit__hint--err">Not a valid base58 pubkey.</span>
              )}
            </label>
          )}

          <label className="adp-deposit__field">
            <div className="adp-deposit__label-row">
              <span className="adp-deposit__label">Amount</span>
              <span className="adp-deposit__balance">
                {walletBalanceLoading
                  ? "—"
                  : walletBalance == null
                    ? ""
                    : `Wallet: ${fmtWalletBalance(walletBalance)} ${tokenSelLabel(tokenSel, customMint)}`}
              </span>
            </div>
            <input
              type="number"
              inputMode="decimal"
              step="any"
              min="0"
              className="adp-deposit__input"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={inFlight || state === "done"}
            />
          </label>

          <button
            type="button"
            className="btn btn--primary adp-deposit__cta"
            onClick={onDeposit}
            disabled={!canSubmit}
          >
            {state === "building" ? "Building tx…"
              : state === "signing" ? "Approve in Phantom…"
              : state === "confirming" ? "Confirming…"
              : state === "done" ? "Deposited ✓"
              : "Deposit →"}
          </button>

          {state === "error" && errMsg && (
            <div className="adp-deposit__err" role="alert">
              <Hallmark size={11} tone="oxblood" /> {errMsg}
            </div>
          )}
          {state === "done" && txSig && (
            <a
              className="adp-deposit__txlink"
              href={SOLSCAN_TX(txSig)}
              target="_blank"
              rel="noopener noreferrer"
            >
              View transaction on Solscan →
            </a>
          )}
        </div>

        <div className="adp-deposit__or" aria-hidden>
          <span className="adp-deposit__or-rule" />
          <span className="adp-deposit__or-text">or send manually</span>
          <span className="adp-deposit__or-rule" />
        </div>
        <div className="adp-deposit__addr">
          <code className="adp-deposit__addr-code">{address}</code>
          <button className="btn btn--ghost btn--small" onClick={onCopy}>
            {copied ? "Copied ✓" : "Copy address"}
          </button>
        </div>
        <div className="adp-modal__actions">
          <button className="btn btn--ghost" onClick={onClose}>
            {state === "done" ? "Done" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}

function shortMint(mint: string): string {
  if (mint === "So11111111111111111111111111111111111111112") return "SOL";
  if (mint === "BRjpCHtyQLNCo8gqRUr8jtdAj5AjPYQaoqbvcZiHok1k" || mint === "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v") return "USDC";
  return `${mint.slice(0, 4)}…${mint.slice(-4)}`;
}

function tokenSelLabel(sel: TokenSel, customMint: string): string {
  if (sel === "SOL") return "SOL";
  if (sel === "USDC") return "USDC";
  return shortMint(customMint.trim());
}

function fmtWalletBalance(n: number): string {
  if (n === 0) return "0";
  if (n < 0.0001) return n.toExponential(2);
  if (n < 1) return n.toFixed(6).replace(/0+$/, "").replace(/\.$/, "");
  if (n < 1000) return n.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function frameworkLabel(f: string): string {
  switch (f) {
    case "sak": return "Solana Agent Kit";
    case "mcp": return "MCP";
    case "eliza": return "ElizaOS";
    case "virtuals": return "Virtuals";
    case "griffain": return "Griffain";
    default: return f;
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function PageStyles() {
  return (
    <style>{`
      .adp { display: flex; flex-direction: column; gap: var(--s-5); padding-bottom: var(--s-9); }
      .adp-loading { padding: var(--s-9); text-align: center; }

      .adp-crumb { display: flex; align-items: center; gap: var(--s-3); font: var(--type-mono-data); color: var(--slate); }
      .adp-crumb a { color: var(--slate); }
      .adp-crumb a:hover { color: var(--bone); }
      .adp-crumb__sep { color: var(--slate-3); }
      .adp-crumb__name { color: var(--bone); }

      /* Banner */
      .adp-banner {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: var(--s-4);
        align-items: center;
        padding: var(--s-3) var(--s-4);
        background: var(--bg-alert);
        border-left: 3px solid var(--oxblood);
        position: relative;
      }
      .adp-banner--warning { border-left-color: var(--bronze); background: var(--bronze-tint); }
      .adp-banner__body { display: flex; align-items: baseline; gap: var(--s-3); flex-wrap: wrap; }
      .adp-banner__msg { font: var(--type-body); color: var(--bone); }
      .adp-banner__msg code { font: var(--type-mono-data); color: var(--oxblood); }
      .adp-banner__cta { font: var(--type-label); letter-spacing: var(--tracking-label); text-transform: uppercase; color: var(--bone); border-bottom: 1px solid var(--oxblood); padding-bottom: 1px; }

      /* ZONE 1 — Header (sigil | identity | kill-switch | Holdings)
         4 columns: identity flexes, kill-switch sits center, Holdings has shade contrast */
      .adp-zone-header {
        display: grid;
        grid-template-columns: auto 1fr auto 280px;
        gap: var(--s-4);
        align-items: center;
        background: var(--bg-elev);
        border: 1px solid var(--line-soft);
        padding: var(--s-3) var(--s-4);
      }
      .adp-zone-header--pending { grid-template-columns: auto 1fr; }
      .adp-head__right {
        align-self: stretch;
        display: flex; flex-direction: column;
        gap: var(--s-2);
        padding: var(--s-3);
        background: var(--bg-elev2);
        border-left: 1px solid var(--line-soft);
        margin: calc(var(--s-3) * -1) calc(var(--s-4) * -1);
        margin-left: 0;
      }
      .adp-head__sigil-frame {
        width: 88px; height: 88px;
        border: 1px solid var(--line);
        background: var(--bg-elev2);
        display: flex; align-items: center; justify-content: center;
        position: relative;
        flex-shrink: 0;
      }
      .adp-head__sigil-frame::before, .adp-head__sigil-frame::after {
        content: '';
        position: absolute; width: 6px; height: 6px;
        border: 1px solid var(--line);
      }
      .adp-head__sigil-frame::before { top: -1px; left: -1px; border-right: 0; border-bottom: 0; }
      .adp-head__sigil-frame::after  { bottom: -1px; right: -1px; border-left: 0; border-top: 0; }

      .adp-head__id { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
      .adp-head__statusrow { display: inline-flex; align-items: center; gap: var(--s-2); margin-bottom: 0; }
      .adp-head__statuslabel {
        font-family: var(--font-mono);
        font-size: 10px;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        font-weight: 500;
      }
      .adp-head__pending-dot {
        width: 8px; height: 8px; border-radius: 999px;
        background: var(--bronze);
        animation: adp-pending-pulse 1.6s ease-in-out infinite;
        display: inline-block;
      }
      @keyframes adp-pending-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      @media (prefers-reduced-motion: reduce) {
        .adp-head__pending-dot { animation: none; }
      }
      .adp-head__name {
        font-family: var(--font-display);
        font-style: italic;
        font-size: 30px;
        line-height: 1.05;
        margin: 0 0 2px;
        color: var(--bone);
      }
      .adp-head__meta {
        font: var(--type-body);
        color: var(--slate);
        display: flex;
        gap: var(--s-2);
        flex-wrap: wrap;
      }
      .adp-head__meta .num { color: var(--bone); }
      .adp-head__last {
        display: inline-flex; align-items: center; gap: var(--s-2);
        margin-top: 4px;
        font: var(--type-mono-data);
        color: var(--slate-2);
      }
      .live-dot { width: 6px; height: 6px; border-radius: 999px; background: var(--bronze); animation: ds-pulse 1.6s ease-in-out infinite; display: inline-block; }
      @keyframes ds-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
      @media (prefers-reduced-motion: reduce) {
        .live-dot { animation: none; }
      }

      .adp-head__actions { display: flex; gap: var(--s-1); justify-content: flex-end; align-items: flex-start; }
      .adp-head__terminal {
        font: var(--type-kicker);
        letter-spacing: var(--tracking-kicker);
        text-transform: uppercase;
        padding: 6px 12px;
        border: 1px solid var(--oxblood);
        color: var(--oxblood);
        background: var(--oxblood-tint);
        font-size: 11px;
      }
      .adp-head__terminal--archived { color: var(--slate); border-color: var(--line); background: transparent; }

      /* Header inline refs (absorbed into identity column for cohesion) */
      .adp-head__refs {
        display: flex; align-items: center; gap: var(--s-2);
        flex-wrap: wrap;
        margin-top: var(--s-1);
        padding-top: var(--s-2);
        border-top: 1px dashed var(--line-soft);
        font-size: 11px;
      }

      /* Kill-switch — Altheia's moat. Center column of header zone. */
      .adp-head__killswitch {
        display: flex; flex-direction: column;
        align-items: center; justify-content: center;
        gap: var(--s-2);
        padding: 0 var(--s-3);
        border-left: 1px dashed var(--line-soft);
        border-right: 1px dashed var(--line-soft);
        align-self: stretch;
      }
      .adp-killswitch__row { display: flex; gap: var(--s-2); }
      .adp-killswitch__btn { padding: var(--s-2) var(--s-4); font-size: 11px; }
      .adp-killswitch__hint {
        font: var(--type-kicker);
        letter-spacing: var(--tracking-kicker);
        text-transform: uppercase;
        color: var(--slate-2);
        font-size: 9.5px;
        text-align: center;
      }
      .adp-killswitch__hint--err { color: var(--oxblood); letter-spacing: 0.04em; text-transform: none; font-size: 11px; }
      .adp-modal__select {
        background: var(--bg);
        border: 1px solid var(--line);
        padding: var(--s-2) var(--s-3);
        font: var(--type-body);
        color: var(--bone);
      }
      .adp-modal__select:focus { outline: none; border-color: var(--oxblood); }
      .adp-modal__select:disabled { opacity: 0.5; }
      .adp-head__last-inline { display: inline-flex; align-items: center; gap: var(--s-2); }

      /* ZONE 3 — API key compact horizontal strip (full width, single row tall) */
      .adp-apikey-strip {
        display: flex; align-items: center; gap: var(--s-4);
        padding: var(--s-3) var(--s-4);
        background: var(--bg-elev2);
        border: 1px solid var(--line-soft);
      }
      .adp-apikey-strip__title {
        font-family: var(--font-display);
        font-style: italic;
        font-size: 18px;
        margin: 0;
        color: var(--bone);
        flex-shrink: 0;
      }
      .adp-apikey-strip__text {
        font: var(--type-body-small);
        color: var(--slate);
        margin: 0;
        flex: 1;
        min-width: 0;
      }
      .adp-apikey-strip__text code {
        font-family: var(--font-mono); color: var(--bone);
        background: var(--bg); padding: 1px 6px; font-size: 0.9em;
      }
      .adp-apikey-strip__btn { flex-shrink: 0; }
      @media (max-width: 768px) {
        .adp-apikey-strip { flex-direction: column; align-items: flex-start; gap: var(--s-2); }
      }

      /* Policy card internal grid — half / half */
      .adp-policy-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--s-5);
        align-items: start;
      }

      /* Holdings — inline pane (no nested card; lives within header zone) */
      .adp-holdings-pane {
        display: flex; flex-direction: column;
        gap: var(--s-2);
      }
      .adp-holdings-pane__head {
        display: flex; justify-content: space-between; align-items: baseline;
        padding-bottom: var(--s-1);
        border-bottom: 1px dashed var(--line-soft);
      }
      .adp-holdings-pane__title {
        font-family: var(--font-display);
        font-style: italic;
        font-size: 18px;
        margin: 0;
        color: var(--bone);
      }
      .adp-holdings-pane__shared-note {
        font: var(--type-body-small);
        color: var(--slate-2);
        margin: 6px 0 0;
        line-height: 1.4;
      }
      .adp-holdings-pane__rows {
        display: flex; flex-direction: column;
        gap: 2px;
        max-height: 88px;
        overflow-y: auto;
      }
      .adp-holdings-pane__row {
        display: flex; justify-content: space-between; align-items: baseline;
        font: var(--type-body-small);
      }
      .adp-holdings-pane__asset {
        font-family: var(--font-mono); font-size: 10px;
        letter-spacing: 0.05em;
        color: var(--bone);
      }
      .adp-holdings-pane__row .num { color: var(--bone); font-family: var(--font-mono); font-size: 12px; }
      .adp-holdings-pane__addr {
        padding-top: var(--s-1);
        border-top: 1px dashed var(--line-soft);
      }
      .adp-holdings-pane__actions {
        display: flex; gap: var(--s-2); justify-content: flex-end;
        padding-top: var(--s-1);
      }
      .adp-holdings-pane__msg { font: var(--type-body-small); color: var(--slate); }
      .adp-holdings-pane__msg--err { color: var(--oxblood); }
      .adp-holdings-pane__msg--success { color: var(--bone); padding-top: var(--s-1); border-top: 1px solid var(--bronze); }

      /* Tiny buttons for header + holdings actions (smaller than btn--small) */
      .adp-tiny-btn {
        display: inline-flex; align-items: center;
        padding: 4px 10px;
        font: var(--type-label);
        font-size: 9.5px;
        letter-spacing: var(--tracking-label);
        text-transform: uppercase;
        background: transparent;
        color: var(--bone);
        border: 1px solid var(--line);
        cursor: pointer;
        transition: border-color 160ms;
      }
      .adp-tiny-btn:hover:not(:disabled) { border-color: var(--bone); }
      .adp-tiny-btn:disabled { opacity: 0.45; cursor: not-allowed; }
      .adp-tiny-btn--primary { background: var(--bronze); color: var(--text-on-ink); border-color: var(--bronze); }
      .adp-tiny-btn--primary:hover:not(:disabled) { background: var(--bronze-deep); border-color: var(--bronze-deep); }
      .adp-tiny-btn--danger { color: var(--oxblood); border-color: var(--oxblood); }
      .adp-tiny-btn--danger:hover:not(:disabled) { background: var(--oxblood); color: var(--text-on-ink); }

      /* Audit card content — table inside, capped height with internal scroll */
      .adp-card__head--audit { flex-wrap: wrap; }
      .adp-card__body--audit { padding: 0; max-height: 480px; overflow-y: auto; }
      .adp-card__body--audit .adp-audit-table-wrap { overflow-x: auto; }
      .adp-card__foot--note { justify-content: flex-start; }

      /* Integration card content */
      .adp-card__body--integration { gap: var(--s-2); }
      .adp-integration__text { font: var(--type-body-small); color: var(--bone); margin: 0; }
      .adp-integration__text--muted { color: var(--slate); }
      .adp-integration__text code {
        font-family: var(--font-mono); color: var(--bone);
        background: var(--bg); padding: 1px 6px; border-radius: 2px; font-size: 0.85em;
      }

      .adp-card {
        background: var(--bg-elev);
        border: 1px solid var(--line-soft);
        display: flex;
        flex-direction: column;
      }
      .adp-card__head {
        display: flex; justify-content: space-between; align-items: end;
        gap: var(--s-3);
        padding: var(--s-3) var(--s-4);
        border-bottom: 1px solid var(--line-soft);
      }
      .adp-card__title {
        font-family: var(--font-display);
        font-style: italic;
        font-size: 22px;
        margin: 4px 0 0;
        color: var(--bone);
        line-height: 1.05;
      }
      .adp-card__refresh { font: var(--type-mono-data); font-size: 11px; }
      .adp-card__body {
        padding: var(--s-4);
        display: flex; flex-direction: column; gap: var(--s-3);
        flex: 1;
      }
      .adp-card__body--policy { gap: var(--s-2); }
      .adp-card__foot {
        display: flex; align-items: center; gap: var(--s-3);
        padding: var(--s-3) var(--s-4);
        border-top: 1px dashed var(--line-soft);
        flex-wrap: wrap;
      }
      .adp-card__foot--actions { gap: var(--s-2); justify-content: flex-end; }
      .adp-card__foot-note { font: var(--type-body-small); color: var(--slate); }
      .adp-card__foot-note code { font: var(--type-mono-data); color: var(--slate); }

      /* Policy sub-sections inside card body — tight stacking */
      .adp-policy-section { display: flex; flex-direction: column; gap: var(--s-1); padding-bottom: var(--s-2); border-bottom: 1px dashed var(--line-soft); }
      .adp-policy-section:last-child { padding-bottom: 0; border-bottom: 0; }
      .adp-policy-empty { font: var(--type-body-small); color: var(--slate-2); }
      .adp-policy-table { width: 100%; border-collapse: collapse; }
      .adp-policy-table th, .adp-policy-table td {
        padding: 6px var(--s-2);
        text-align: left;
        font: var(--type-body-small);
      }
      .adp-policy-table th {
        font: var(--type-kicker);
        letter-spacing: var(--tracking-kicker);
        text-transform: uppercase;
        color: var(--slate-2);
        font-weight: 600;
        padding-bottom: var(--s-2);
        border-bottom: 1px solid var(--line-soft);
      }
      .adp-policy-table td { color: var(--bone); }
      /* Zebra-stripe rows with subtle gradient on odd rows */
      .adp-policy-table tbody tr:nth-child(odd) td {
        background: linear-gradient(90deg, var(--bg-elev2) 0%, rgba(0,0,0,0) 100%);
      }
      .adp-policy-asset {
        font-family: var(--font-mono);
        font-size: 11px;
        letter-spacing: 0.06em;
        color: var(--bone);
      }
      .adp-programs { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; }
      .adp-programs__row {
        display: flex; align-items: center; gap: var(--s-3);
        padding: 6px var(--s-2);
        min-width: 0;
      }
      .adp-programs__row:nth-child(odd) {
        background: linear-gradient(90deg, var(--bg-elev2) 0%, rgba(0,0,0,0) 100%);
      }
      .adp-programs__name { font: var(--type-body-small); color: var(--bone); flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .adp-programs__tag {
        font-family: var(--font-mono);
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: var(--slate-2);
        font-size: 9.5px;
        flex-shrink: 0;
      }
      .adp-programs__addr { font-size: 11px; color: var(--slate-2); flex-shrink: 0; }
      .adp-blocked-list {
        list-style: none; padding: 0; margin: 0;
        display: flex; flex-wrap: wrap; gap: var(--s-2);
      }
      .adp-blocked-chip {
        padding: 2px 8px;
        border: 1px solid var(--oxblood);
        color: var(--oxblood);
        background: var(--oxblood-tint);
      }

      /* Holdings card */
      .adp-holdings { display: flex; flex-direction: column; gap: var(--s-3); }
      .adp-holdings--empty .adp-holdings__amount { color: var(--slate-2); }
      .adp-holdings__primary {
        display: flex; align-items: baseline; gap: var(--s-2);
      }
      .adp-holdings__amount {
        font-family: var(--font-display);
        font-style: italic;
        font-size: 40px;
        line-height: 1;
        color: var(--bone);
      }
      .adp-holdings__unit {
        font-family: var(--font-display);
        font-style: italic;
        font-size: 18px;
        color: var(--slate);
      }
      .adp-holdings__hint { font: var(--type-body-small); color: var(--slate); margin: 0; }
      .adp-holdings__tokens {
        list-style: none; padding: 0; margin: 0;
        display: flex; flex-direction: column; gap: var(--s-2);
        border-top: 1px dashed var(--line-soft);
        padding-top: var(--s-3);
      }
      .adp-holdings__token {
        display: flex; justify-content: space-between; align-items: center;
        font: var(--type-body);
      }
      .adp-holdings__token-asset {
        font-family: var(--font-mono); font-size: 11px;
        background: var(--bg-elev2); padding: 2px 8px;
        color: var(--bone);
      }
      .adp-holdings__token .num { color: var(--bone); }
      .adp-holdings__address {
        display: flex; flex-direction: column; gap: var(--s-1);
        padding-top: var(--s-3);
        border-top: 1px dashed var(--line-soft);
      }
      .adp-holdings__addr-code {
        font-family: var(--font-mono); font-size: 11px; color: var(--slate);
        word-break: break-all; line-height: 1.5;
      }
      .adp-balance-loading, .adp-balance-empty, .adp-balance-err {
        font: var(--type-body); color: var(--slate);
      }
      .adp-balance-err { color: var(--oxblood); }
      .adp-balance-success {
        margin: 0 var(--s-5) var(--s-4);
        padding: var(--s-3);
        background: var(--bg-elev2); border-left: 3px solid var(--bronze);
        font: var(--type-body); color: var(--bone);
      }
      .link-btn { background: none; border: 0; padding: 0; color: var(--bronze); text-decoration: underline; cursor: pointer; font: inherit; }
      .link-btn:disabled { opacity: 0.5; cursor: not-allowed; text-decoration: none; }

      /* Pending state */
      .adp-pending-card {
        background: var(--bg-elev);
        border: 1px solid var(--bronze);
        border-left-width: 3px;
        padding: var(--s-6);
        display: flex; flex-direction: column; gap: var(--s-3);
        max-width: 720px;
      }
      .adp-pending-card__title {
        font-family: var(--font-display); font-style: italic;
        font-size: 24px; margin: 0; color: var(--bone);
      }
      .adp-pending-card__body { font: var(--type-body); color: var(--slate); margin: 0; }
      .adp-pending-card__actions { display: flex; gap: var(--s-3); margin-top: var(--s-3); padding-top: var(--s-3); border-top: 1px dashed var(--line-soft); }

      /* Audit pills */
      .adp-pills { display: inline-flex; gap: 0; border: 1px solid var(--line); }
      .adp-pill {
        font: var(--type-kicker);
        letter-spacing: var(--tracking-kicker);
        text-transform: uppercase;
        padding: 6px 12px;
        background: transparent;
        color: var(--slate);
        border: 0;
        cursor: pointer;
        border-right: 1px solid var(--line);
        display: inline-flex;
        align-items: center;
        gap: var(--s-2);
      }
      .adp-pill:last-child { border-right: 0; }
      .adp-pill:hover { color: var(--bone); }
      .adp-pill--active { background: var(--bg-elev); color: var(--bone); }
      .adp-pill--denied.adp-pill--active { color: var(--oxblood); }
      .adp-pill .num { color: inherit; }

      /* Audit table */
      .adp-audit-table-wrap { overflow-x: auto; }
      .adp-audit-table { width: 100%; border-collapse: collapse; min-width: 640px; }
      .adp-audit-table thead th {
        padding: var(--s-3);
        text-align: left;
        font: var(--type-kicker);
        letter-spacing: var(--tracking-kicker);
        text-transform: uppercase;
        color: var(--slate-2);
        border-bottom: 1px solid var(--line);
        font-weight: 600;
      }
      .adp-audit-table__num, .adp-audit-table__right { text-align: right; }
      .adp-audit-table tbody td {
        padding: var(--s-3);
        font: var(--type-body-small);
        border-bottom: 1px solid var(--line-soft);
        color: var(--bone);
      }
      .adp-audit-table tbody tr { cursor: pointer; }
      .adp-audit-table tbody tr:hover { background: var(--bg-elev); }
      .adp-audit-row--denied { background: var(--oxblood-tint); }
      .adp-audit-row--expanded { background: var(--bg-elev); }
      .adp-audit-row--denied.adp-audit-row--expanded { background: var(--oxblood-tint); border-left: 3px solid var(--oxblood); }
      .adp-audit-table__action { font: var(--type-mono-data); color: var(--bone); }
      .adp-audit-table__mint {
        font: var(--type-kicker);
        letter-spacing: var(--tracking-kicker);
        color: var(--slate);
        font-size: 10.5px;
      }
      .adp-audit-table__link { color: var(--slate-2); margin-left: 4px; }
      .adp-audit-table__result-allowed, .adp-audit-table__result-denied {
        display: inline-flex; align-items: center; gap: var(--s-2);
        font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase;
      }
      .adp-audit-table__result-allowed { color: var(--bronze-deep); }
      .adp-audit-table__result-denied { color: var(--oxblood); }
      .adp-audit-table__chev { color: var(--slate-3); margin-left: var(--s-2); }
      .deny-bar { display: inline-block; width: 8px; height: 1.5px; background: var(--oxblood); }

      .adp-audit-detail { background: var(--oxblood-tint); }
      .adp-audit-detail--denied { border-left: 3px solid var(--oxblood); }
      .adp-audit-detail td { padding: var(--s-4) !important; }
      .adp-audit-detail__grid {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr 1.4fr;
        gap: var(--s-5);
      }
      .adp-audit-detail__grid > div { display: flex; flex-direction: column; gap: 6px; }
      .adp-audit-detail__reason { color: var(--oxblood); }
      .adp-audit-detail__link { color: var(--slate-2); }

      .adp-audit-empty { padding: var(--s-7); text-align: center; color: var(--slate-3); }
      .adp-audit-empty__hint { margin-top: var(--s-2); color: var(--slate-2); font-size: 13px; }

      /* Modals */
      .adp-modal-bg { position: fixed; inset: 0; background: rgba(13, 11, 8, 0.45); display: flex; align-items: center; justify-content: center; z-index: 100; padding: var(--s-4); }
      .adp-modal {
        background: var(--bg-elev2);
        border: 1px solid var(--line);
        padding: var(--s-6);
        max-width: 520px;
        width: 100%;
        max-height: 92vh;
        overflow-y: auto;
        box-shadow: 0 24px 64px rgba(13, 11, 8, 0.18);
      }
      .adp-modal__title {
        font-family: var(--font-display);
        font-style: italic;
        font-size: 28px;
        margin: var(--s-3) 0;
      }
      .adp-modal__body { font: var(--type-body); color: var(--slate); margin-bottom: var(--s-4); }
      .adp-modal__body strong { color: var(--oxblood); }
      .adp-modal__body code {
        font-family: var(--font-mono); color: var(--bone);
        background: var(--bg-elev); padding: 1px 6px; border-radius: 2px; font-size: 0.85em;
      }
      .adp-modal__confirm { display: flex; flex-direction: column; gap: var(--s-2); margin-bottom: var(--s-4); }
      .adp-modal__confirm input {
        background: var(--bg);
        border: 1px solid var(--line);
        padding: var(--s-3);
        font: var(--type-mono-data);
        color: var(--bone);
      }
      .adp-modal__confirm input:focus { outline: none; border-color: var(--oxblood); }
      .adp-modal__actions { display: flex; justify-content: flex-end; gap: var(--s-3); padding-top: var(--s-3); border-top: 1px solid var(--line-soft); }
      .adp-modal__err {
        font: var(--type-body-small);
        color: var(--oxblood);
        background: var(--bg-elev);
        border-left: 2px solid var(--oxblood);
        padding: var(--s-2) var(--s-3);
        margin-bottom: var(--s-3);
      }

      /* Withdraw modal — editable destination + custom-recipient warning */
      .adp-withdraw-input {
        display: flex; flex-direction: column; gap: var(--s-2);
        margin-bottom: var(--s-3);
      }
      .adp-withdraw-input__row {
        display: flex; gap: var(--s-2); align-items: center;
        background: var(--bg); border: 1px solid var(--line);
        padding: var(--s-2) var(--s-3);
      }
      .adp-withdraw-input__field {
        flex: 1; min-width: 0;
        background: transparent; border: 0; outline: none;
        font-family: var(--font-mono); font-size: 12px; color: var(--bone);
        padding: var(--s-1) 0;
      }
      .adp-withdraw-input__field::placeholder { color: var(--slate-2); }
      .adp-withdraw-input__row:focus-within { border-color: var(--bronze); }
      .adp-withdraw-input__reset { font-size: 11px; flex-shrink: 0; white-space: nowrap; }
      .adp-withdraw-input__hint { font: var(--type-body-small); color: var(--slate); }
      .adp-withdraw-input__hint--err { color: var(--oxblood); }

      .adp-withdraw-warn {
        display: flex; flex-direction: column; gap: var(--s-2);
        padding: var(--s-3);
        background: var(--oxblood-tint); border-left: 3px solid var(--oxblood);
        margin-bottom: var(--s-3);
      }
      .adp-withdraw-warn p { font: var(--type-body-small); color: var(--bone); margin: 0; }
      .adp-withdraw-warn strong { color: var(--oxblood); }

      /* Deposit modal */
      .adp-deposit__phantom {
        width: 100%; justify-content: center; margin-bottom: var(--s-3);
      }
      .adp-deposit__form {
        display: flex; flex-direction: column; gap: var(--s-3);
        margin-bottom: var(--s-3);
      }
      .adp-deposit__field { display: flex; flex-direction: column; gap: 6px; }
      .adp-deposit__label-row {
        display: flex; justify-content: space-between; align-items: baseline;
        gap: var(--s-2);
      }
      .adp-deposit__label {
        font: var(--type-kicker);
        letter-spacing: var(--tracking-kicker);
        text-transform: uppercase;
        font-size: 10px;
        color: var(--slate);
      }
      .adp-deposit__balance {
        font-family: var(--font-mono);
        font-size: 10px;
        color: var(--slate-2);
        letter-spacing: 0.02em;
        white-space: nowrap;
      }
      .adp-deposit__select,
      .adp-deposit__input {
        padding: 10px 12px;
        background: var(--bg);
        border: 1px solid var(--line);
        color: var(--bone);
        font: var(--type-body);
        outline: none;
      }
      .adp-deposit__select:focus,
      .adp-deposit__input:focus { border-color: var(--bronze); }
      .adp-deposit__input--mono { font-family: var(--font-mono); font-size: 12px; }
      .adp-deposit__hint { font: var(--type-body-small); color: var(--slate); }
      .adp-deposit__hint--err { color: var(--oxblood); }
      .adp-deposit__cta {
        width: 100%; justify-content: center; margin-top: var(--s-1);
      }
      .adp-deposit__err {
        color: var(--oxblood); font: var(--type-body-small);
        display: inline-flex; align-items: center; gap: var(--s-2);
        padding: var(--s-2) var(--s-3);
        border: 1px solid var(--oxblood);
      }
      .adp-deposit__txlink {
        font: var(--type-body-small); color: var(--bronze);
        border-bottom: 1px solid var(--line-soft); padding-bottom: 1px;
        align-self: flex-start;
      }
      .adp-deposit__txlink:hover { border-bottom-color: var(--bronze); }
      .adp-deposit__or {
        display: flex; align-items: center; gap: var(--s-3);
        margin: var(--s-3) 0;
      }
      .adp-deposit__or-rule { flex: 1; height: 1px; background: var(--line-soft); }
      .adp-deposit__or-text {
        font: var(--type-kicker);
        letter-spacing: var(--tracking-kicker);
        text-transform: uppercase;
        color: var(--slate-2);
        font-size: 10px;
      }
      .adp-deposit__addr {
        display: flex; align-items: center; gap: var(--s-2);
        padding: var(--s-3);
        background: var(--bg); border: 1px solid var(--line);
        margin-bottom: var(--s-4);
      }
      .adp-deposit__addr-code {
        font-family: var(--font-mono); font-size: 11px; color: var(--bone);
        flex: 1; min-width: 0; word-break: break-all;
      }

      /* API key reveal (modal) */
      .adp-apikey-reveal {
        display: flex; align-items: center; gap: var(--s-3); margin-bottom: var(--s-4);
        padding: var(--s-3); background: var(--bg-elev); border: 1px solid var(--line);
      }
      .adp-apikey-reveal__code {
        font-family: var(--font-mono); font-size: 12px; color: var(--bone);
        flex: 1; min-width: 0; overflow-wrap: anywhere;
      }
      .adp-apikey-reveal__copy { flex-shrink: 0; }

      .btn--small { padding: 6px 10px; font-size: 10px; }
      .btn:disabled { opacity: 0.4; cursor: not-allowed; }
      .btn:focus-visible { outline: 2px solid var(--bronze); outline-offset: 2px; }
      .link-btn:focus-visible { outline: 2px solid var(--bronze); outline-offset: 2px; }
      .adp-pill:focus-visible { outline: 2px solid var(--bronze); outline-offset: -2px; position: relative; z-index: 1; }

      .dot-sep { color: var(--slate-3); }

      /* Responsive */
      @media (max-width: 1024px) {
        .adp-policy-grid { grid-template-columns: 1fr; }
        .adp-zone-header {
          grid-template-columns: auto 1fr;
          grid-template-areas: "sigil identity" "killswitch killswitch" "right right";
          align-items: start;
        }
        .adp-zone-header--pending { grid-template-columns: auto 1fr; grid-template-areas: "sigil identity"; }
        .adp-head__sigil-frame { grid-area: sigil; }
        .adp-head__id { grid-area: identity; }
        .adp-head__killswitch {
          grid-area: killswitch;
          flex-direction: row; justify-content: flex-start;
          padding: var(--s-2) 0;
          border-left: 0; border-right: 0;
          border-top: 1px solid var(--line-soft);
          border-bottom: 1px solid var(--line-soft);
        }
        .adp-head__right {
          grid-area: right;
          margin: 0 calc(var(--s-4) * -1) calc(var(--s-3) * -1);
          border-left: 0;
          border-top: 1px solid var(--line-soft);
        }
      }
      @media (max-width: 768px) {
        .adp-zone-header { padding: var(--s-3); gap: var(--s-3); }
        .adp-head__sigil-frame { width: 64px; height: 64px; }
        .adp-head__name { font-size: 26px; }
        .adp-card__title { font-size: 20px; }
        .adp-holdings__amount { font-size: 26px; }
        .adp-audit-detail__grid { grid-template-columns: 1fr 1fr; }
        .adp-audit-table thead th, .adp-audit-table tbody td { padding: var(--s-2); font-size: 11px; }
        .adp-card__head { padding: var(--s-3); }
        .adp-card__body { padding: var(--s-3); }
        .adp-card__foot { padding: var(--s-2) var(--s-3); }
        .adp-head__right { flex-direction: column; }
      }
      @media (max-width: 480px) {
        .adp-banner { grid-template-columns: 1fr; }
        .adp-banner__cta { justify-self: start; }
        .adp-refs-strip { gap: var(--s-2); }
      }
    `}</style>
  );
}
